/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faThLarge,
  faSearch,
  faFileAlt,
  faBookmark,
  faStar,
  faUser,
  faBook,
  faCalendar,
  faCheckCircle,
  faEdit,
  faUpload,
  faArrowUp,
  faPaperPlane,
  faCalendarCheck,
  faEye,
  faMapMarkerAlt,
  faBriefcase,
  faChartLine,
  faSave,
  faTimes,
  faCode,
  faBuilding,
  faGraduationCap,
  faCertificate,
  faLanguage,
  faLink,
  faEllipsisV,
  faCog,
  faInfoCircle,
  faExternalLinkAlt,
  faBars,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { faHandPeace } from '@fortawesome/free-regular-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import '../styles/JobSeekerDashboard.css';
import '../styles/InternDashboardMobile.css';
import '../styles/JobSeekerDashboardMobile.css';
import notificationApi from '../api/notificationApi';
import messageApi from '../api/messageApi';
import JobCard from '../components/JobCard';
import MessagingSystem from '../components/MessagingSystem';
import SettingsPage from '../components/SettingsPage';
import ApplicationTrackerTable from '../components/ApplicationTrackerTable';
import StatCard from '../components/StatCard';
import DashboardJobRow from '../components/DashboardJobRow';
import DashboardApplicationRow from '../components/DashboardApplicationRow';
import ProfessionalMessaging from '../components/ProfessionalMessaging';
import EmployerRecruiterHeader from '../components/EmployerRecruiterHeader';
import JobSeekerDashboardMobileHeader from '../components/JobSeekerDashboardMobileHeader';
import ResumeUpload from '../components/ResumeUpload';
import InterviewCalendar from '../components/InterviewCalendar';
import EmployerFilterDropdown from '../components/EmployerFilterDropdown';

const BROWSE_JOBS_SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'relevance', label: 'Relevance' },
  { value: 'salary-high', label: 'Salary: High to Low' },
];

const RECOMMENDED_MATCH_OPTIONS = [
  { value: 'all', label: 'All Recommendations' },
  { value: 'high', label: 'High Match (90%+)' },
  { value: 'good', label: 'Good Match (70–89%)' },
  { value: 'fair', label: 'Fair Match (50–69%)' },
];

const RECOMMENDED_SORT_OPTIONS = [
  { value: 'match-score', label: 'Match Score' },
  { value: 'recent', label: 'Most Recent' },
  { value: 'salary-high', label: 'Salary: High to Low' },
  { value: 'company-name', label: 'Company Name' },
];

const TRACKER_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const TRACKER_LOCATION_OPTIONS = [
  { value: 'all', label: 'All Locations' },
  { value: 'nairobi', label: 'Nairobi, Kenya' },
  { value: 'mombasa', label: 'Mombasa, Kenya' },
  { value: 'remote', label: 'Remote' },
];

const TRACKER_SORT_OPTIONS = [
  { value: 'recent', label: 'Most Recent' },
  { value: 'salary-high', label: 'Salary: High to Low' },
];

const renderFilterDropdown = (useCustomDropdown, value, onChange, options, className = '') => (
  useCustomDropdown ? (
    <EmployerFilterDropdown
      showCounts={false}
      value={value}
      onChange={onChange}
      options={options}
      className={className}
    />
  ) : (
    <select
      className={`filter-select ${className}`.trim()}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  )
);

const openJobDetailsTab = (job) => {
  if (!job) return;
  const jobId = String(job._id || job.id || job.job_id || '').trim();
  const baseUrl = window.location.origin;

  if (jobId) {
    window.open(`${baseUrl}/job-details/${jobId}`, '_blank', 'noopener,noreferrer');
  } else {
    window.open(`${baseUrl}/job-details`, '_blank', 'noopener,noreferrer');
  }
};

// Company display helpers (shared across job listings)
const isCompanyConfidential = (job) => {
  if (!job || typeof job !== 'object') return false;
  const explicitHideFlags = [
    job.show_company_name === false,
    job.showCompanyName === false,
    job.hideCompany === true,
    job.hideCompanyName === true,
  ];
  if (explicitHideFlags.some(Boolean)) {
    return true;
  }
  const otherFlags = [
    job.confidential,
    job.isConfidential,
    job.companyConfidential,
    job.company_confidential,
    job.company_is_confidential
  ];
  if (otherFlags.some((v) => v === true || v === 'true' || v === 1 || v === '1')) {
    return true;
  }
  const rawName = (job.company_name || job.companyName || job.company || '').toString().toLowerCase();
  return rawName.includes('confidential');
};

const getDisplayCompanyName = (job) => {
  if (isCompanyConfidential(job)) {
    return 'Confidential';
  }
  return job.company_name || job.companyName || job.company || '—';
};

const sanitizeJobTitle = (title) => {
  if (!title) return title;
  return String(title).replace(/^Updated\s*[-:]?\s*/i, '');
};

const formatJobPostedDate = (job) => {
  const posted = job.posted_date || job.postedDate || job.postedAt || job.created_at || job.createdAt || job.datePosted || job.date_posted;
  if (!posted) return null;
  try {
    const date = new Date(posted);
    if (isNaN(date.getTime())) return null;
    return `${date.toLocaleDateString('en-GB')} | ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return null;
  }
};

const getJobMatchScore = (job) => job.matchScore || job.match_score || job.relevance_score || 0;

const formatJobPostedRelative = (job) => {
  const posted = job.posted_date || job.postedDate || job.postedAt || job.created_at || job.createdAt || job.datePosted || job.date_posted;
  if (!posted) return null;
  try {
    const date = new Date(posted);
    if (Number.isNaN(date.getTime())) return null;
    const diffDays = Math.floor((Date.now() - date.getTime()) / 86400000);
    if (diffDays <= 0) return 'Posted today';
    if (diffDays === 1) return 'Posted 1d ago';
    if (diffDays < 7) return `Posted ${diffDays}d ago`;
    if (diffDays < 30) return `Posted ${Math.floor(diffDays / 7)}w ago`;
    return `Posted ${date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
  } catch {
    return null;
  }
};

const getDashboardGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatInterviewRailDate = (interview) => {
  const raw = interview.interview_date || interview.interviewDate;
  if (!raw) return 'Date TBD';
  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return 'Date TBD';
    const time = interview.interview_start_time || interview.interview_time || '';
    const dateLabel = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return time ? `${dateLabel} · ${time}` : dateLabel;
  } catch {
    return 'Date TBD';
  }
};

const MOBILE_LAYOUT_BREAKPOINT = 768;

const ACTION_MENU_VIEWPORT_PADDING = 10;
const ACTION_MENU_WIDTH = 188;

const getActionMenuPosition = (triggerRect) => {
  const padding = ACTION_MENU_VIEWPORT_PADDING;
  const viewportWidth = window.innerWidth;
  const menuWidth = Math.min(ACTION_MENU_WIDTH, viewportWidth - padding * 2);

  let left = triggerRect.left;
  if (left + menuWidth > viewportWidth - padding) {
    left = triggerRect.right - menuWidth;
  }
  left = Math.max(padding, Math.min(left, viewportWidth - menuWidth - padding));

  const estimatedMenuHeight = 140;
  let top = triggerRect.bottom + 4;
  if (top + estimatedMenuHeight > window.innerHeight - padding) {
    top = Math.max(padding, triggerRect.top - estimatedMenuHeight - 4);
  }

  return { left, top, width: menuWidth };
};

// Browse Jobs Section
const BrowseJobsSection = ({ jobs, onApply, onSave, onViewDetails, savedJobIds, appliedJobIds, initialSearch = '', useCustomDropdown = false }) => {
  const safeJobsProp = Array.isArray(jobs) ? jobs : [];
  console.log('🔍 BrowseJobsSection rendered with jobs:', safeJobsProp.length, 'isArray:', Array.isArray(jobs));
  const [filteredJobs, setFilteredJobs] = useState(safeJobsProp);
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });
  const [filters, setFilters] = useState({
    search: initialSearch,
    type: 'all',
    location: 'all',
    experience: 'all',
    sort: 'recent'
  });

  // Helper to extract a comparable numeric salary value from various salary formats
  const getJobMaxSalary = (job) => {
    if (!job || typeof job !== 'object') return 0;

    // 1) Structured numeric fields
    const structuredSources = [
      job.salary_range?.max,
      job.salary?.max,
      job.max_salary,
      job.maxSalary,
      job.salaryMax,
      job.stipend_max,
      job.stipendMax
    ].filter((v) => typeof v === 'number' && !Number.isNaN(v));

    if (structuredSources.length > 0) {
      return Math.max(...structuredSources);
    }

    // 2) Numeric strings or range strings
    const stringSources = [
      job.salary_range,
      job.salary,
      job.salaryDisplay,
      job.salary_display,
      job.stipend,
      job.stipendDisplay,
      job.compensation,
      job.compensationDisplay
    ].filter(Boolean);

    let best = 0;
    for (const raw of stringSources) {
      const value = String(raw);
      const matches = value.replace(/,/g, '').match(/\d+(?:\.\d+)?/g);
      if (!matches) continue;
      const nums = matches.map((n) => Number(n)).filter((n) => !Number.isNaN(n));
      if (nums.length === 0) continue;
      const localMax = Math.max(...nums);
      if (localMax > best) best = localMax;
    }

    return best || 0;
  };

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Apply filters whenever jobs or filters change
  useEffect(() => {
    const safeJobs = Array.isArray(jobs) ? jobs : [];
    console.log('🔄 BrowseJobsSection: Processing jobs. Jobs:', safeJobs.length, 'Filters:', filters);
    
    if (safeJobs.length === 0) {
      console.log('⚠️ BrowseJobsSection: No jobs to filter');
      setFilteredJobs([]);
      return;
    }
    
    let filtered = [...safeJobs];

    // Apply search filter first
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase().trim();
      filtered = filtered.filter(job => {
        const jobTitle = (job.job_title || job.title || '').toLowerCase();
        const companyName = getDisplayCompanyName(job).toLowerCase();
        const description = (job.description || job.job_description || '').toLowerCase();
          const location = (job.location || job.job_location || job.city || '').toLowerCase();
        const skills = Array.isArray(job.skills) ? job.skills.join(' ').toLowerCase() : '';
        const requirements = (job.requirements || job.job_requirements || '').toLowerCase();
        
        return (
          jobTitle.includes(searchTerm) ||
          companyName.includes(searchTerm) ||
          description.includes(searchTerm) ||
          location.includes(searchTerm) ||
          skills.includes(searchTerm) ||
          requirements.includes(searchTerm)
        );
      });
    }

    // Filter logic for type, location, and experience removed - only search and sort remain

    // Apply sorting
    if (filters.sort === 'salary-high') {
      filtered.sort((a, b) => {
        const aMax = getJobMaxSalary(a);
        const bMax = getJobMaxSalary(b);
        return bMax - aMax;
      });
    } else if (filters.sort === 'relevance') {
      // Relevance sort - maintain original order (already sorted by relevance from API)
      // No additional sorting needed
    } else if (filters.sort === 'recent' || !filters.sort) {
      filtered.sort((a, b) => {
        // Try multiple possible date fields (same as display logic)
        const aPosted = a.posted_date || a.postedDate || a.postedAt || a.created_at || a.createdAt || a.datePosted || a.date_posted;
        const bPosted = b.posted_date || b.postedDate || b.postedAt || b.created_at || b.createdAt || b.datePosted || b.date_posted;
        
        // Handle missing dates - put them at the end
        if (!aPosted && !bPosted) return 0;
        if (!aPosted) return 1; // a goes to end
        if (!bPosted) return -1; // b goes to end
        
        const aDate = new Date(aPosted);
        const bDate = new Date(bPosted);
        
        // Handle invalid dates - put them at the end
        const aValid = !isNaN(aDate.getTime());
        const bValid = !isNaN(bDate.getTime());
        
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1; // a goes to end
        if (!bValid) return -1; // b goes to end
        
        // Sort by most recent first (descending order)
        return bDate.getTime() - aDate.getTime();
      });
    }

    console.log('📊 BrowseJobsSection: Filtered jobs count:', filtered.length, 'out of', safeJobs.length, 'total jobs');
    setFilteredJobs(filtered);
  }, [filters, jobs]);

  const handleMenuOpen = (event, job) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition(getActionMenuPosition(rect));
    setActionMenuData(job);
  };

  return (
    <div className="browse-jobs-page">
      <div className="browse-jobs-toolbar filters">
        <input
          type="text"
          placeholder="Search jobs by title, company, location, skills..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="browse-jobs-search-input"
        />
        {renderFilterDropdown(
          useCustomDropdown,
          filters.sort,
          (value) => setFilters({ ...filters, sort: value }),
          BROWSE_JOBS_SORT_OPTIONS,
          'browse-jobs-sort-dropdown'
        )}
      </div>

      {Array.isArray(filteredJobs) && filteredJobs.length > 0 ? (
        <div className="applications-table-wrapper">
          <table className="applications-table">
            <thead>
              <tr>
                <th style={{ width: '26%' }}>Role / Company</th>
                <th style={{ width: '18%' }}>Location</th>
                <th style={{ width: '18%' }}>Type</th>
                <th style={{ width: '18%' }}>Posted On</th>
                <th style={{ width: '20%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job) => (
                <tr className="application-row" key={job._id || job.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div className="candidate-avatar" style={{
                        width: '42px',
                        height: '42px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #FF8A65, #14b8a6)',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '16px'
                      }}>
                        {(job.company_name || job.companyName || job.company || 'J').charAt(0).toUpperCase()}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <strong style={{ color: '#0f172a' }}>
                          {sanitizeJobTitle(job.job_title || job.title || 'Job')}
                        </strong>
                        <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '14px' }}>
                          {getDisplayCompanyName(job)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {job.location || job.job_location || job.city || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#475569' }}>
                      <span>{job.job_type || job.jobType || job.type || 'Full-time'}</span>
                      <small style={{ color: '#94a3b8' }}>{job.experience_level || job.experienceRequired || job.experience_required || ''}</small>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        // Try multiple possible date fields
                        const posted = job.posted_date || job.postedDate || job.postedAt || job.created_at || job.createdAt || job.datePosted || job.date_posted;
                        if (!posted) {
                          console.log('No date found for job:', job._id || job.id, 'Available fields:', Object.keys(job));
                          return '—';
                        }
                        
                        try {
                        const date = new Date(posted);
                          // Check if date is valid
                          if (isNaN(date.getTime())) {
                            console.log('Invalid date for job:', job._id || job.id, 'Date value:', posted);
                            return '—';
                          }
                        return `${date.toLocaleDateString('en-GB')} | ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                        } catch (error) {
                          console.error('Error parsing date:', error, 'for job:', job._id || job.id, 'Date value:', posted);
                          return '—';
                        }
                      })()}
                    </div>
                  </td>
                  <td style={{ position: 'relative', overflow: 'visible' }}>
                    <button
                      className="actions-toggle"
                      onClick={(e) => handleMenuOpen(e, job)}
                    >
                      Actions ▾
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faSearch} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
            <h3>No Jobs Found</h3>
            <p style={{ marginBottom: '10px' }}>
              {filters.search 
                ? `No jobs match "${filters.search}". Try a different search term or adjust your filters.`
                : 'Try adjusting your filters to see more results.'}
            </p>
            <p style={{ color: '#999', fontSize: '14px' }}>Check back later for new opportunities!</p>
          </div>
        </div>
      )}
      {actionMenuData && createPortal(
        <div
          className="actions-menu actions-menu-portal"
          style={{
            position: 'fixed',
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`,
            width: `${actionMenuPosition.width || ACTION_MENU_WIDTH}px`
          }}
        >
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={async () => {
              if (onApply) await onApply(actionMenuData);
              setActionMenuData(null);
            }}
            disabled={appliedJobIds.has(String(actionMenuData._id || actionMenuData.id))}
          >
            {appliedJobIds.has(String(actionMenuData._id || actionMenuData.id)) ? 'Applied' : 'Apply'}
          </button>
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={() => {
              if (onViewDetails) onViewDetails(actionMenuData);
              setActionMenuData(null);
            }}
          >
            View Details
          </button>
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={async () => {
              if (onSave) await onSave(actionMenuData._id || actionMenuData.id);
              setActionMenuData(null);
            }}
          >
            {savedJobIds.has(actionMenuData._id || actionMenuData.id) ? 'Unsave Job' : 'Save Job'}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};

// Recommended Jobs Section
const RecommendedJobsSection = React.forwardRef(({ recommendedJobs, profileCompletion, onApply, onSave, onViewDetails, savedJobIds, appliedJobIds, navigate, useCustomDropdown = false }, ref) => {
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });
  const [isMobileView, setIsMobileView] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= MOBILE_LAYOUT_BREAKPOINT
  );
  const [filters, setFilters] = useState({
    matchScore: 'all',
    sort: 'match-score'
  });
  const [filteredJobs, setFilteredJobs] = useState([]);

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth <= MOBILE_LAYOUT_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  const handleMenuOpen = (event, job) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition(getActionMenuPosition(rect));
    setActionMenuData(job);
  };

  // Apply filters and sorting to recommended jobs
  useEffect(() => {
    const safeJobs = Array.isArray(recommendedJobs) ? recommendedJobs : [];
    let filtered = [...safeJobs];

    // Filter by match score
    if (filters.matchScore !== 'all') {
      filtered = filtered.filter(job => {
        const matchScore = job.matchScore || job.match_score || job.relevance_score || 0;
        if (filters.matchScore === 'high') {
          return matchScore >= 90;
        } else if (filters.matchScore === 'good') {
          return matchScore >= 70 && matchScore < 90;
        } else if (filters.matchScore === 'fair') {
          return matchScore >= 50 && matchScore < 70;
        }
        return true;
      });
    }

    // Apply sorting
    if (filters.sort === 'recent') {
      filtered.sort((a, b) => {
        const aPosted = a.posted_date || a.postedDate || a.postedAt || a.created_at || a.createdAt || a.datePosted || a.date_posted;
        const bPosted = b.posted_date || b.postedDate || b.postedAt || b.created_at || b.createdAt || b.datePosted || b.date_posted;
        
        if (!aPosted && !bPosted) return 0;
        if (!aPosted) return 1;
        if (!bPosted) return -1;
        
        const aDate = new Date(aPosted);
        const bDate = new Date(bPosted);
        
        const aValid = !isNaN(aDate.getTime());
        const bValid = !isNaN(bDate.getTime());
        
        if (!aValid && !bValid) return 0;
        if (!aValid) return 1;
        if (!bValid) return -1;
        
        return bDate.getTime() - aDate.getTime();
      });
    } else if (filters.sort === 'salary-high') {
      filtered.sort((a, b) => {
        const aMax = a.salary_range?.max || a.salary?.max || a.salary_range || a.salary || 0;
        const bMax = b.salary_range?.max || b.salary?.max || b.salary_range || b.salary || 0;
        return bMax - aMax;
      });
    } else if (filters.sort === 'company-name') {
      filtered.sort((a, b) => {
        const aCompany = (a.company_name || a.companyName || a.company || '').toLowerCase();
        const bCompany = (b.company_name || b.companyName || b.company || '').toLowerCase();
        return aCompany.localeCompare(bCompany);
      });
    } else if (filters.sort === 'match-score') {
      // Sort by match score (highest first)
      filtered.sort((a, b) => {
        const aScore = a.matchScore || a.match_score || a.relevance_score || 0;
        const bScore = b.matchScore || b.match_score || b.relevance_score || 0;
        return bScore - aScore;
      });
    }

    setFilteredJobs(filtered);
  }, [recommendedJobs, filters]);

  const renderRecommendedMobileCard = (job, idx) => {
    const jobId = String(job._id || job.id || idx);
    const matchScore = getJobMatchScore(job);
    const postedLabel = formatJobPostedDate(job);
    const isApplied = appliedJobIds.has(jobId);
    const isSaved = savedJobIds.has(job._id || job.id);

    return (
      <li
        key={jobId}
        className="list-item recommended-job-item"
        onClick={() => onViewDetails?.(job)}
      >
        <div className="job-info">
          <div className="job-card-top-row">
            <div className="job-title-row">
              <div className="list-title">{sanitizeJobTitle(job.job_title || job.title) || 'Job'}</div>
              {matchScore > 0 && (
                <span
                  className="job-match-badge"
                  style={{
                    background: matchScore >= 70 ? '#10b981' : matchScore >= 40 ? '#f59e0b' : '#6b7280',
                  }}
                >
                  {Math.round(matchScore)}% Match
                </span>
              )}
            </div>
            <div className="job-actions">
              <button
                type="button"
                className="btn btn-primary btn-sm job-apply-btn"
                disabled={isApplied}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (onApply && !isApplied) await onApply(job);
                }}
              >
                {isApplied ? 'Applied' : 'Apply'}
              </button>
              <button
                type="button"
                className={`btn btn-secondary btn-sm job-save-btn${isSaved ? ' saved' : ''}`}
                aria-label={isSaved ? 'Unsave job' : 'Save job'}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (onSave) await onSave(job._id || job.id);
                }}
              >
                <FontAwesomeIcon icon={faBookmark} />
              </button>
            </div>
          </div>
          <div className="list-subtitle">{getDisplayCompanyName(job)}</div>
          <div className="job-meta">
            <span className="job-location">
              <FontAwesomeIcon icon={faMapMarkerAlt} />
              {job.location || job.job_location || job.city || 'Not specified'}
            </span>
            <span className="job-type">
              <FontAwesomeIcon icon={faBriefcase} />
              {job.job_type || job.jobType || job.type || 'Full-time'}
            </span>
          </div>
          {postedLabel && (
            <div className="recommended-job-posted">Posted {postedLabel}</div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="recommended-jobs-page-section" style={{ marginTop: '-1rem', paddingTop: '0' }}>
      <div className="card recommended-jobs-page-card recommended-section" ref={ref}>
        <div className="card-header recommended-header" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <h3 className="card-title recommended-title" style={{ textAlign: 'center', width: '100%', display: 'block', margin: '0 auto' }}>AI-Recommended Jobs</h3>
          <div className="filters recommended-jobs-filters">
            {renderFilterDropdown(
              useCustomDropdown,
              filters.matchScore,
              (value) => setFilters({ ...filters, matchScore: value }),
              RECOMMENDED_MATCH_OPTIONS,
              'recommended-match-dropdown'
            )}
            {renderFilterDropdown(
              useCustomDropdown,
              filters.sort,
              (value) => setFilters({ ...filters, sort: value }),
              RECOMMENDED_SORT_OPTIONS,
              'recommended-sort-dropdown'
            )}
          </div>
        </div>
        <div className="card-content">
          {filteredJobs && filteredJobs.length > 0 ? (
            isMobileView ? (
              <ul className="list recommended-jobs-mobile-list">
                {filteredJobs.map(renderRecommendedMobileCard)}
              </ul>
            ) : (
            <div className="applications-table-wrapper">
              <table className="applications-table">
                <thead>
                  <tr>
                    <th style={{ width: '26%' }}>Role / Company</th>
                    <th style={{ width: '18%' }}>Location</th>
                    <th style={{ width: '18%' }}>Type</th>
                    <th style={{ width: '18%' }}>Posted On</th>
                    <th style={{ width: '20%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map((job, idx) => (
                    <tr className="application-row" key={job._id || idx}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div className="candidate-avatar" style={{
                            width: '42px',
                            height: '42px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #FF8A65, #14b8a6)',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: '700',
                            fontSize: '16px'
                          }}>
                            {(job.company_name || job.companyName || job.company || 'J').charAt(0).toUpperCase()}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <strong style={{ color: '#0f172a' }}>
                              {job.job_title || job.title || 'Job'}
                            </strong>
                            <span style={{ color: '#0ea5e9', fontWeight: 600, fontSize: '14px' }}>
                              {getDisplayCompanyName(job)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ color: '#475569' }}>
                          {job.location || job.job_location || job.city || 'Not specified'}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: '#475569' }}>
                          <span>{job.job_type || job.jobType || job.type || 'Full-time'}</span>
                          <small style={{ color: '#94a3b8' }}>{job.experience_level || job.experienceRequired || job.experience_required || 'Not specified'}</small>
                        </div>
                      </td>
                      <td>
                        <div style={{ color: '#475569' }}>
                          {formatJobPostedDate(job) || '—'}
                        </div>
                      </td>
                      <td style={{ position: 'relative', overflow: 'visible' }}>
                        <button
                          className="actions-toggle"
                          onClick={(e) => handleMenuOpen(e, job)}
                        >
                          Actions ▾
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          ) : (recommendedJobs && recommendedJobs.length > 0 && filteredJobs.length === 0) ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faSearch} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
              <h3>No Jobs Match Your Filters</h3>
              <p style={{ marginBottom: '10px' }}>
                Try adjusting your filters to see more results.
              </p>
              <button 
                className="btn btn-primary" 
                onClick={() => setFilters({ matchScore: 'all', sort: 'match-score' })}
              >
                Reset Filters
              </button>
            </div>
          ) : (profileCompletion >= 80) ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faStar} className="empty-icon" />
              <h3>No AI Recommendations Yet</h3>
              <p>We're working on finding personalized job recommendations for you. Check back soon!</p>
              <button className="btn btn-primary" onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faEdit} /> Update Profile
              </button>
            </div>
          ) : (
            <div className="empty-state">
              <FontAwesomeIcon icon={faStar} className="empty-icon" />
              <h3>No AI Recommendations Yet</h3>
              <p>Complete your profile to get personalized job recommendations powered by AI.</p>
              <button className="btn btn-primary" onClick={() => navigate('/profile')}>
                <FontAwesomeIcon icon={faUser} /> Complete Profile
              </button>
            </div>
          )}
        </div>
      </div>
      {actionMenuData && createPortal(
        <div
          className="actions-menu actions-menu-portal"
          style={{
            position: 'fixed',
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`,
            width: `${actionMenuPosition.width || ACTION_MENU_WIDTH}px`
          }}
        >
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={async () => {
              if (onApply) await onApply(actionMenuData);
              setActionMenuData(null);
            }}
            disabled={appliedJobIds.has(String(actionMenuData._id || actionMenuData.id))}
          >
            {appliedJobIds.has(String(actionMenuData._id || actionMenuData.id)) ? 'Applied' : 'Apply'}
          </button>
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={() => {
              if (onViewDetails) onViewDetails(actionMenuData);
              setActionMenuData(null);
            }}
          >
            View Details
          </button>
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={async () => {
              if (onSave) await onSave(actionMenuData._id || actionMenuData.id);
              setActionMenuData(null);
            }}
          >
            {savedJobIds.has(actionMenuData._id || actionMenuData.id) ? 'Unsave Job' : 'Save Job'}
          </button>
        </div>,
        document.body
      )}
    </div>
  );
});

// Interviews Section - shows interview details set by employer
const InterviewsSection = ({ interviews }) => {
  // Collect all meeting links (Zoom, Google Meet, or generic) so they're all visible
  const getMeetingLinks = (interview) => {
    const raw = (url) => (url && String(url).trim()) || '';
    const links = [];
    const seen = new Set();
    const add = (url, type) => {
      const u = raw(url);
      if (u && !seen.has(u)) {
        seen.add(u);
        links.push({ url: u, type });
      }
    };
    add(interview.zoom_link || interview.zoomLink, 'zoom');
    add(interview.google_meet_link || interview.googleMeetLink, 'meet');
    add(interview.meeting_link || interview.meetingLink, 'meeting');
    add(interview.interview_link || interview.interviewLink, 'meeting');
    // If we only have one link from "meeting", detect Zoom/Meet from URL for label
    if (links.length === 1 && links[0].type === 'meeting') {
      const u = links[0].url.toLowerCase();
      if (u.includes('zoom.us') || u.includes('zoom.com')) links[0].type = 'zoom';
      else if (u.includes('meet.google') || u.includes('meet.google.com')) links[0].type = 'meet';
    }
    return links;
  };

  const normalizeInterview = (interview) => {
    if (!interview) return null;
    const dateRaw = interview.interview_date || interview.interviewDate;
    if (!dateRaw) return null;
    const [datePart] = String(dateRaw).split('T');
    const parts = datePart.split('-').map(Number);
    if (parts.length < 3 || parts.some(Number.isNaN)) return null;
    const [year, month, day] = parts;

    const startTime = interview.interview_start_time || interview.interviewStartTime || interview.interview_time || '';
    const endTime = interview.interview_end_time || interview.interviewEndTime || '';
    const [startHour = 0, startMinute = 0] = startTime ? String(startTime).split(':').map(Number) : [0, 0];
    const startDate = new Date(year, month - 1, day, startHour || 0, startMinute || 0);

    const meetingLinks = getMeetingLinks(interview);
    const interviewLink = meetingLinks.length > 0 ? meetingLinks[0].url : (interview.interview_link || interview.interviewLink || '');

    return {
      ...interview,
      interview_date: datePart,
      interview_start_time: startTime,
      interview_end_time: endTime,
      interview_date_obj: startDate,
      interview_mode: (interview.interview_mode || interview.interviewMode || 'online').toLowerCase(),
      interview_title: interview.job_title || interview.jobTitle || 'Interview',
      company_name: interview.company_name || interview.companyName || 'Company',
      interview_notes: interview.interview_notes || interview.interviewNotes || '',
      interview_link: interviewLink,
      meeting_links: meetingLinks,
      interview_location: interview.interview_location || interview.interviewLocation || ''
    };
  };

  const getMeetingLinkLabel = (linkType) => {
    if (linkType === 'zoom') return 'Zoom Meeting';
    if (linkType === 'meet') return 'Google Meet';
    return 'Meeting Link';
  };

  const formatTimeRange = (start, end) => {
    if (!start && !end) return 'Time TBD';
    const format = (value) => {
      if (!value) return '';
      const [hours, minutes] = String(value).split(':').map(Number);
      if (Number.isNaN(hours)) return value;
      return new Date(0, 0, 0, hours, minutes || 0).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };
    const startStr = format(start);
    const endStr = format(end);
    return endStr ? `${startStr} - ${endStr}` : startStr || endStr || 'Time TBD';
  };

  const normalizedInterviews = (interviews || [])
    .map(normalizeInterview)
    .filter(Boolean)
    .sort((a, b) => a.interview_date_obj - b.interview_date_obj);

  return (
    <div>
      <div className="alert info" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.08)', border: '1px solid rgba(99, 102, 241, 0.2)', marginBottom: '20px' }}>
        <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '24px', color: '#6366f1', flexShrink: 0 }} />
        <div>
          <strong>Tip:</strong> Research the company and prepare examples of your projects before the interview.
        </div>
      </div>

      {normalizedInterviews.length > 0 ? (
        <div className="interviews-grid">
          {normalizedInterviews.map((interview, index) => (
            <div key={index} className="card interview-detail-card">
              <div className="interview-detail-header">
                <div>
                  <span className="interview-detail-date">
                    {interview.interview_date_obj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </span>
                  <h3>{interview.interview_title}</h3>
                  <p className="interview-detail-company">{interview.company_name}</p>
                </div>
                <span className={`interview-mode-badge ${interview.interview_mode}`}>
                  {interview.interview_mode}
                </span>
              </div>
              <div className="interview-detail-body">
                <div>
                  <label>Time</label>
                  <p>{formatTimeRange(interview.interview_start_time, interview.interview_end_time)}</p>
                </div>
                {interview.interview_notes && (
                  <div>
                    <label>Notes</label>
                    <p>{interview.interview_notes}</p>
                  </div>
                )}
                {(interview.meeting_links && interview.meeting_links.length > 0 ? interview.meeting_links : (interview.interview_link ? [{ url: interview.interview_link, type: 'meeting' }] : [])).map((item, linkIdx) => (
                  <div key={linkIdx} className="interview-meeting-link">
                    <label>{getMeetingLinkLabel(item.type)}</label>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className={`meeting-link meeting-link-${item.type}`}>
                      {item.type === 'zoom' && <span className="meeting-link-label">Join Zoom Meeting</span>}
                      {item.type === 'meet' && <span className="meeting-link-label">Join Google Meet</span>}
                      {item.type === 'meeting' && <span className="meeting-link-label">Join Meeting</span>}
                      <span className="meeting-link-url">{item.url}</span>
                      <FontAwesomeIcon icon={faExternalLinkAlt} style={{ marginLeft: '6px', fontSize: '12px' }} />
                    </a>
                  </div>
                ))}
                {interview.interview_location && (
                  <div>
                    <label>Location</label>
                    <p>{interview.interview_location}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faCalendarCheck} style={{ fontSize: '64px', color: '#ccc' }} />
            <h3>No Interviews Scheduled</h3>
            <p>Your upcoming interviews will appear here once employers set interview details for your applications.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// Application Tracker Section
const ApplicationTrackerSection = React.forwardRef(({ useCustomDropdown = false }, ref) => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMenuData, setActionMenuData] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ left: 0, top: 0 });
  const [statusPopup, setStatusPopup] = useState(null);
  const previousStatusesRef = React.useRef({});
  const shownMessagesRef = React.useRef(new Set());
  const [filters, setFilters] = useState({
    status: 'all',
    location: 'all',
    sort: 'recent'
  });

  const getStatusMessage = (status, jobTitle, companyName) => {
    const statusLower = (status || '').toLowerCase();
    const title = jobTitle || 'this position';
    const company = companyName || 'the company';
    
    switch (statusLower) {
      case 'hired':
      case 'accepted':
        return {
          message: `🎉 Congratulations! You've been hired for ${title} at ${company}! Welcome to the team!`,
          type: 'success',
          icon: '🎉'
        };
      case 'offered':
        return {
          message: `🎊 Great news! You've received a job offer for ${title} at ${company}! Check your email for details.`,
          type: 'success',
          icon: '🎊'
        };
      case 'interview_scheduled':
      case 'interview scheduled':
      case 'interview':
        return {
          message: `🚀 Exciting! Your interview has been scheduled for ${title} at ${company}. Prepare well and show your best!`,
          type: 'info',
          icon: '🚀'
        };
      case 'shortlisted':
        return {
          message: `⭐ Excellent! You've been shortlisted for ${title} at ${company}. You're one step closer to landing this role!`,
          type: 'success',
          icon: '⭐'
        };
      case 'reviewed':
      case 'reviewing':
        return {
          message: `👀 Your application for ${title} at ${company} is being reviewed. The employer will get back to you soon!`,
          type: 'info',
          icon: '👀'
        };
      case 'rejected':
        return {
          message: `💪 Thank you for applying to ${title} at ${company}. While this wasn't the right fit, keep applying - your perfect opportunity is coming!`,
          type: 'neutral',
          icon: '💪'
        };
      default:
        return null; // No message for pending or unknown statuses
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Please log in to view applications');
      }

      const response = await axios.get(
        buildApiUrl('/api/application-tracker/tracker/job-seeker/applications'),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const applicationsData = response.data.applications || response.data || [];
      const sortedApplications = applicationsData.sort((a, b) => {
        const dateA = new Date(a.appliedAt || a.applied_at || 0);
        const dateB = new Date(b.appliedAt || b.applied_at || 0);
        return dateB - dateA;
      });
      
      // Check for status changes and show popup only when status actually changed (not on first load)
      const stableAppId = (app) => String(app._id || app.id || '');
      sortedApplications.forEach(app => {
        const appId = stableAppId(app);
        const currentStatus = (app.status || 'pending').toLowerCase();
        const previousStatus = previousStatusesRef.current[appId];
        
        // Only show popup when we had a previous status AND it changed to something new (not pending).
        // This prevents showing "Congratulations!" every time the user opens Application Tracker for an already-hired application.
        const isActualChange = previousStatus !== undefined && previousStatus !== currentStatus && currentStatus !== 'pending';
        if (isActualChange) {
          const messageKey = `${appId}-${currentStatus}`;
          const storedShown = localStorage.getItem(`statusMessage-${messageKey}`);
          if (!storedShown && !shownMessagesRef.current.has(messageKey)) {
            const statusInfo = getStatusMessage(
              currentStatus,
              app.jobTitle || app.job_title,
              app.company_name || app.company || app.companyName
            );
            if (statusInfo) {
              localStorage.setItem(`statusMessage-${messageKey}`, 'true');
              shownMessagesRef.current.add(messageKey);
              setStatusPopup({
                status: currentStatus,
                jobTitle: app.jobTitle || app.job_title || 'this position',
                companyName: app.company_name || app.company || app.companyName || 'the company',
                message: statusInfo.message,
                type: statusInfo.type,
                icon: statusInfo.icon
              });
            }
          }
        }
        previousStatusesRef.current[appId] = currentStatus;
      });
      
      setApplications(sortedApplications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
    const interval = setInterval(fetchApplications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const closeMenu = (event) => {
      if (
        !event.target.closest('.actions-menu-portal') &&
        !event.target.closest('.actions-toggle')
      ) {
        setActionMenuData(null);
      }
    };
    const handleScroll = () => setActionMenuData(null);

    document.addEventListener('mousedown', closeMenu);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', closeMenu);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    let filtered = [...applications];

    // Apply filters
    if (filters.status !== 'all') {
      filtered = filtered.filter(app => {
        const status = (app.status || '').toLowerCase();
        return status === filters.status.toLowerCase();
      });
    }

    if (filters.location !== 'all') {
      if (filters.location === 'remote') {
        filtered = filtered.filter(app => {
          const location = (app.location || app.job_location || '').toLowerCase();
          return location.includes('remote');
        });
      } else {
        filtered = filtered.filter(app => {
          const location = (app.location || app.job_location || '').toLowerCase();
          return location.includes(filters.location.toLowerCase());
        });
      }
    }

    // Apply sorting
    if (filters.sort === 'recent') {
      filtered.sort((a, b) => {
        const aDate = new Date(a.appliedAt || a.applied_at || a.appliedDate || a.applied_date || a.createdAt || 0);
        const bDate = new Date(b.appliedAt || b.applied_at || b.appliedDate || b.applied_date || b.createdAt || 0);
        return bDate - aDate;
      });
    } else if (filters.sort === 'salary-high') {
      filtered.sort((a, b) => {
        const aSalary = a.salary_range?.max || a.salary?.max || a.internshipDetails?.stipend?.max || 0;
        const bSalary = b.salary_range?.max || b.salary?.max || b.internshipDetails?.stipend?.max || 0;
        return bSalary - aSalary;
      });
    }

    setFilteredApplications(filtered);
  }, [filters, applications]);

  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return '#f59e0b';
      case 'reviewed':
      case 'reviewing':
        return '#3b82f6';
      case 'shortlisted':
        return '#10b981';
      case 'rejected':
        return '#ef4444';
      case 'accepted':
      case 'hired':
        return '#059669';
      default:
        return '#6b7280';
    }
  };

  const formatStipend = (app) => {
    const stipend = app.internshipDetails?.stipend || app.salary_range || app.salary || app.stipend_range || app.stipend;
    if (!stipend) return 'Not specified';
    if (typeof stipend === 'object') {
      const min = stipend.min || stipend.minimum || 0;
      const max = stipend.max || stipend.maximum || 0;
      if (min && max) {
        const currency = stipend.currency || 'USD';
        const symbol = currency === 'USD' ? '$' : currency === 'KES' || currency === 'KSh' ? 'KSh' : currency;
        return `${symbol}${min.toLocaleString()} - ${symbol}${max.toLocaleString()}`;
      } else if (min) {
        const currency = stipend.currency || 'USD';
        const symbol = currency === 'USD' ? '$' : currency === 'KES' || currency === 'KSh' ? 'KSh' : currency;
        return `${symbol}${min.toLocaleString()}+`;
      }
    }
    if (typeof stipend === 'string') return stipend;
    if (typeof stipend === 'number') return `$${stipend.toLocaleString()}`;
    return 'Not specified';
  };

  const resolveJobIdentifier = (app) => {
    const paths = ['job_id', 'jobId', 'job._id', 'job.id', 'internshipId', 'internship_id'];
    for (const path of paths) {
      const value = path.includes('.') 
        ? path.split('.').reduce((acc, key) => acc?.[key], app)
        : app[path];
      if (value) return String(value);
    }
    return null;
  };

  const handleMenuOpen = (event, application) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setActionMenuPosition(getActionMenuPosition(rect));
    setActionMenuData(application);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <ThemedLoadingSpinner theme="default" size="large" text="Loading your applications..." />
      </div>
    );
  }

  return (
    <div ref={ref}>
      {/* Status Update Popup Card */}
      {statusPopup && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 10000,
          maxWidth: '450px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
          border: `2px solid ${statusPopup.type === 'success' ? '#10b981' : statusPopup.type === 'info' ? '#3b82f6' : '#6b7280'}`,
          padding: '20px',
          animation: 'slideInRight 0.3s ease-out'
        }}>
          <style>{`
            @keyframes slideInRight {
              from {
                transform: translateX(100%);
                opacity: 0;
              }
              to {
                transform: translateX(0);
                opacity: 1;
              }
            }
          `}</style>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '15px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '18px',
                fontWeight: '700',
                color: statusPopup.type === 'success' ? '#10b981' : statusPopup.type === 'info' ? '#3b82f6' : '#6b7280',
                marginBottom: '8px'
              }}>
                STATUS
              </div>
              <div style={{
                fontSize: '14px',
                color: '#475569',
                lineHeight: '1.6'
              }}>
                {statusPopup.message}
              </div>
            </div>
            <button
              onClick={() => setStatusPopup(null)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#94a3b8',
                cursor: 'pointer',
                padding: '0',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f1f5f9';
                e.target.style.color = '#64748b';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#94a3b8';
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="filters application-tracker-filters">
        {renderFilterDropdown(
          useCustomDropdown,
          filters.status,
          (value) => setFilters({ ...filters, status: value }),
          TRACKER_STATUS_OPTIONS,
          'tracker-status-dropdown'
        )}
        {renderFilterDropdown(
          useCustomDropdown,
          filters.location,
          (value) => setFilters({ ...filters, location: value }),
          TRACKER_LOCATION_OPTIONS,
          'tracker-location-dropdown'
        )}
        {renderFilterDropdown(
          useCustomDropdown,
          filters.sort,
          (value) => setFilters({ ...filters, sort: value }),
          TRACKER_SORT_OPTIONS,
          'tracker-sort-dropdown'
        )}
      </div>

      {filteredApplications && filteredApplications.length > 0 ? (
        <div className="applications-table-wrapper">
          <table className="applications-table">
            <thead>
              <tr>
                <th style={{ width: '20%' }}>Company</th>
                <th style={{ width: '20%' }}>Position</th>
                <th style={{ width: '16%' }}>Location</th>
                <th style={{ width: '16%' }}>Stipend</th>
                <th style={{ width: '14%' }}>Applied</th>
                <th style={{ width: '10%' }}>Status</th>
                <th style={{ width: '4%' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map((application) => (
                <tr className="application-row" key={application._id || application.id}>
                  <td>
                    <div>
                      <strong style={{ color: '#0f172a' }}>
                        {application.company_name || application.company || application.companyName || 'N/A'}
                      </strong>
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569', fontWeight: 500 }}>
                      {application.jobTitle || application.job_title || 'Position'}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {application.location || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {formatStipend(application)}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#475569' }}>
                      {(() => {
                        const applied = application.appliedAt || application.applied_at || application.appliedDate || application.applied_date;
                        if (!applied) return '—';
                        const date = new Date(applied);
                        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                      })()}
                    </div>
                  </td>
                  <td>
                    <div style={{ 
                      display: 'inline-flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      backgroundColor: `${getStatusColor(application.status)}20`,
                      border: `1px solid ${getStatusColor(application.status)}`,
                      color: getStatusColor(application.status),
                      fontSize: '12px',
                      fontWeight: 600
                    }}>
                      <span style={{
                        width: '6px',
                        height: '6px',
                        borderRadius: '50%',
                        backgroundColor: getStatusColor(application.status)
                      }}></span>
                      {(application.status || 'Pending').charAt(0).toUpperCase() + (application.status || 'Pending').slice(1).toLowerCase()}
                    </div>
                  </td>
                  <td style={{ position: 'relative', overflow: 'visible', whiteSpace: 'nowrap' }}>
                    <button
                      className="actions-toggle"
                      onClick={(e) => handleMenuOpen(e, application)}
                      style={{ padding: '6px 12px', fontSize: '13px' }}
                    >
                      Actions ▾
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: '64px', color: '#ccc', marginBottom: '20px' }} />
            <h3>No Applications Found</h3>
            <p style={{ marginBottom: '10px' }}>Try adjusting your filters to see more results.</p>
            <p style={{ color: '#999', fontSize: '14px' }}>You haven't applied to any jobs yet.</p>
          </div>
        </div>
      )}
      {actionMenuData && createPortal(
        <div
          className="actions-menu actions-menu-portal"
          style={{
            position: 'fixed',
            left: `${actionMenuPosition.left}px`,
            top: `${actionMenuPosition.top}px`,
            width: `${actionMenuPosition.width || ACTION_MENU_WIDTH}px`
          }}
        >
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={() => {
              const jobId = resolveJobIdentifier(actionMenuData);
              if (jobId) {
                const baseUrl = window.location.origin;
                window.open(`${baseUrl}/job-details/${jobId}`, '_blank', 'noopener,noreferrer');
              } else {
                alert('Job details are unavailable for this application.');
              }
              setActionMenuData(null);
            }}
          >
            View Details
          </button>
          <button
            type="button"
            className="actions-menu-item btn-custom-size"
            onClick={async () => {
              if (window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
                try {
                  const token = localStorage.getItem('token');
                  const applicationId = actionMenuData._id || actionMenuData.id;
                  
                  if (!applicationId) {
                    alert('Error: Unable to identify this application.');
                    setActionMenuData(null);
                    return;
                  }

                  console.log('🗑️ Withdrawing application:', applicationId);

                  const response = await axios.delete(
                    buildApiUrl(`/api/applications/${applicationId}`),
                    { headers: { Authorization: `Bearer ${token}` } }
                  );

                  if (response.status === 200) {
                    console.log('✅ Application withdrawn successfully');
                    
                    // Remove from local state immediately
                    setApplications(prev => {
                      const updated = prev.filter(app => {
                        const appId = app._id || app.id;
                        return String(appId) !== String(applicationId);
                      });
                      console.log('📋 Updated applications count:', updated.length, 'from', prev.length);
                      return updated;
                    });
                    
                    // Also update filtered applications
                    setFilteredApplications(prev => {
                      const updated = prev.filter(app => {
                        const appId = app._id || app.id;
                        return String(appId) !== String(applicationId);
                      });
                      return updated;
                    });
                    
                    // Refresh applications from server to ensure consistency
                    setTimeout(async () => {
                      try {
                        await fetchApplications();
                      } catch (refreshError) {
                        console.error('Error refreshing applications after withdrawal:', refreshError);
                      }
                    }, 500);
                    
                    alert('Application withdrawn successfully.');
                  } else {
                    console.error('❌ Unexpected response status:', response.status);
                    alert('Failed to withdraw application. Please try again.');
                  }
                } catch (error) {
                  console.error('❌ Error withdrawing application:', error);
                  console.error('Error details:', {
                    message: error.message,
                    response: error.response?.data,
                    status: error.response?.status
                  });
                  
                  if (error.response?.status === 404) {
                    alert('Application not found. It may have already been withdrawn.');
                    // Refresh applications list
                    await fetchApplications();
                  } else if (error.response?.status === 403) {
                    alert('You are not authorized to withdraw this application.');
                  } else if (error.response?.data?.error) {
                    alert(`Error: ${error.response.data.error}`);
                  } else {
                    alert('Failed to withdraw application. Please try again.');
                  }
                }
              }
              setActionMenuData(null);
            }}
            style={{ color: '#ef4444' }}
          >
            Withdraw Application
          </button>
        </div>,
        document.body
      )}
    </div>
  );
});

ApplicationTrackerSection.displayName = 'ApplicationTrackerSection';

const JobSeekerDashboard = () => {
  console.log('🚀 JobSeekerDashboard component rendering...');
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: viewUserId } = useParams(); // Get userId from URL if admin is viewing another user's dashboard
  const isAdminView = !!viewUserId && (user?.role === 'admin' || user?.userType === 'admin');
  
  // State management
  const [activeSection, setActiveSection] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {
      applicationsSent: 0,
      interviewsScheduled: 0,
      profileViews: 0,
      savedJobs: 0
    },
    profileCompletion: 0,
    recentApplications: [],
    recommendedJobs: [],
    allJobs: [],
    savedJobs: [],
    profileViews: [],
    user: {},
    careerResources: [],
    interviews: []
  });
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [savedJobIds, setSavedJobIds] = useState(new Set());
  const [appliedJobIds, setAppliedJobIds] = useState(new Set());
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  // Profile state - moved to dedicated MyProfile page
  // These are kept for backward compatibility but not used in dashboard UI
  const [profileForm, setProfileForm] = useState({});
  const [education, setEducation] = useState([]);
  const [experience, setExperience] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [professionalLinks, setProfessionalLinks] = useState([]);
  const [references, setReferences] = useState([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showResumeUpload, setShowResumeUpload] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  // Inline style helpers to precisely match screenshot without relying on global CSS
  const containerStyle = { maxWidth: '1200px', margin: '0 auto' };
  const elevatedCardStyle = {
    border: '1px solid #edf2f7',
    borderRadius: '16px',
    boxShadow: '0 10px 30px rgba(17, 24, 39, 0.06)'
  };
  const profileHeaderStyle = {
    borderRadius: '16px',
    minHeight: '220px',
    boxShadow: '0 10px 30px rgba(102, 126, 234, 0.25)'
  };
  const pillTagStyle = {
    display: 'inline-block',
    background: '#fed7aa',
    color: '#ea580c',
    padding: '8px 14px',
    borderRadius: '999px',
    margin: '5px',
    fontSize: '14px',
    border: '1px solid #cfe2ff'
  };
  const fieldViewModeStyle = () => (
    editMode ? {} : { background: '#f7f9fc', borderColor: '#e6eaf1', color: '#333' }
  );

  const storageKey = () => `favorites_${localStorage.getItem('userId') || 'guest'}`;
  const loadSavedJobs = () => {
    try {
      const stored = localStorage.getItem(storageKey());
      if (!stored) return new Set();
      const arr = JSON.parse(stored);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  };
  const persistSavedJobs = (setObj) => {
    try { localStorage.setItem(storageKey(), JSON.stringify([...setObj])); } catch {}
  };
  const toggleSaveJob = (jobId) => {
    setSavedJobIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId); else next.add(jobId);
      persistSavedJobs(next);
      setDashboardData(d => ({
        ...d,
        stats: { ...d.stats, savedJobs: next.size }
      }));
      return next;
    });
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Section anchors for smooth in-page navigation
  const sectionRefs = useRef({
    dashboardTop: React.createRef(),
    statsGrid: React.createRef(),
    recommended: React.createRef(),
    recentApplications: React.createRef(),
    profileCompletion: React.createRef(),
    applications: React.createRef(),
    saved: React.createRef(),
    interviews: React.createRef(),
    messages: React.createRef(),
    profile: React.createRef(),
    resume: React.createRef(),
    resources: React.createRef(),
    settings: React.createRef()
  });

  // Navigation items
  const navigationItems = [
    { id: 'dashboard', icon: faThLarge, label: 'Dashboard', section: 'dashboard' },
    { id: 'jobs', icon: faSearch, label: 'Browse Jobs', section: 'jobs', badge: 'NEW' },
    { id: 'tracker', icon: faFileAlt, label: 'Application Tracker', section: 'applications', badge: dashboardData.stats.applicationsSent > 0 ? dashboardData.stats.applicationsSent.toString() : null },
    { id: 'saved', icon: faBookmark, label: 'Saved Jobs', section: 'saved', badge: dashboardData.stats.savedJobs > 0 ? dashboardData.stats.savedJobs.toString() : null },
    { id: 'interviews', icon: faCalendarCheck, label: 'Interviews', section: 'interviews', badge: dashboardData.stats.interviewsScheduled > 0 ? dashboardData.stats.interviewsScheduled.toString() : null },
    { id: 'recommended', icon: faStar, label: 'Recommended', section: 'recommended' },
    // TEMPORARILY DISABLED - Akshar Coins
    // { id: 'coins', icon: faCoins, label: 'Akshar Coins', section: 'coins', isRoute: true, route: '/promo-code#aksharCoins' },
    // TEMPORARILY DISABLED - Messages
    // { id: 'messages', icon: faEnvelope, label: 'Messages', section: 'messages', badge: unreadMessages > 0 ? unreadMessages.toString() : null },
    { id: 'profile', icon: faUser, label: 'My Profile', section: 'profile', isRoute: true, route: '/profile' },
    // { id: 'resume', icon: faFilePdf, label: 'Resume/CV', section: 'resume', disabled: true },
    { id: 'resources', icon: faBook, label: 'Career Resources', section: 'resources' },
    { id: 'settings', icon: faCog, label: 'Settings', section: 'settings' }
  ];

  const handleNavClick = (item) => {
    setShowMobileSidebar(false);
    if (item.isRoute && item.route) {
      navigate(item.route);
      return;
    }
    setActiveSection(item.section);
    const map = {
      dashboard: 'dashboardTop',
      applications: 'applications',
      recommended: 'recommended',
      jobs: 'recommended',
      profile: 'profile',
      saved: 'saved',
      interviews: 'interviews',
      messages: 'messages',
      resume: 'resume',
      resources: 'resources',
      settings: 'settings'
    };
    const refKey = map[item.section];
    const ref = refKey ? sectionRefs.current[refKey] : null;
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleResumeUploaded = async (resumeData, aiRecommendations) => {
    console.log('📄 Resume uploaded successfully:', resumeData);
    console.log('📋 Resume data structure:', {
      hasPersonalInfo: !!resumeData?.personal_info,
      hasSkills: !!resumeData?.skills,
      hasExperience: !!resumeData?.experience,
      hasEducation: !!resumeData?.education,
      hasSummary: !!resumeData?.summary,
      experienceCount: resumeData?.experience?.length || 0,
      educationCount: resumeData?.education?.length || 0
    });
    
    try {
      // Populate profile from resume data
      console.log('🔄 Populating profile from resume data...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const populateResponse = await axios.post(
        buildApiUrl('/api/jobseeker/populate-from-resume'),
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('📡 Populate response:', {
        status: populateResponse.status,
        success: populateResponse.data?.success,
        message: populateResponse.data?.message,
        error: populateResponse.data?.error
      });
      
      if (populateResponse.data?.success) {
        console.log('✅ Profile populated from resume successfully');
        console.log('📊 Populated data:', populateResponse.data);
        setShowResumeUpload(false);
        alert('✅ Resume uploaded and profile populated successfully! Refreshing page to show your data...');
        // Give a moment for the alert to show, then reload
        setTimeout(() => {
          window.location.href = '/profile'; // Navigate to profile page instead of reload
        }, 1000);
      } else {
        const errorMsg = populateResponse.data?.error || 'Failed to populate profile';
        console.error('❌ Populate failed:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('❌ Error populating profile from resume:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      setShowResumeUpload(false);
      
      // Show detailed error to user
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      alert(`⚠️ Resume uploaded successfully, but profile population failed:\n\n${errorMessage}\n\nYou can manually edit your profile to add the information.`);
      
      // Still navigate to profile page so user can manually edit
      setTimeout(() => {
        window.location.href = '/profile';
      }, 1500);
    }
  };

  const authHeaders = () => {
    const token = localStorage.getItem('token') || '';
    console.log('🔑 Token from localStorage:', token ? 'Present' : 'Missing');
    console.log('🔑 Token length:', token.length);
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  // Set active section based on route
  useEffect(() => {
    const path = location.pathname;
    if (path === '/application-tracker') {
      setActiveSection('applications');
    } else if (path === '/allJobs') {
      setActiveSection('jobs');
    } else if (location.state && (location.state.query || location.state.location || location.state.category)) {
      // If search parameters exist, switch to jobs section
      setActiveSection('jobs');
    } else {
      setActiveSection('dashboard');
    }
  }, [location.pathname, location.state]);

  // Load dashboard data
  useEffect(() => {
    console.log('🔄 useEffect triggered - loading dashboard data');
    console.log('🔍 Current user:', user);
    console.log('🔑 Token exists:', !!localStorage.getItem('token'));
    console.log('🔍 viewUserId:', viewUserId, 'isAdminView:', isAdminView);
    
    const fetchProfile = async () => {
      try {
        // If admin is viewing another user's dashboard, include userId parameter
        const url = isAdminView && viewUserId 
          ? buildApiUrl(`/api/jobseeker/profile?userId=${viewUserId}`)
          : buildApiUrl('/api/jobseeker/profile');
        const res = await axios.get(url, { headers: authHeaders() });
        console.log('✅ Jobseeker profile data:', res?.data);
        return res?.data || null;
      } catch (e) {
        console.log('⚠️ Jobseeker profile failed, trying general profile...');
        try {
          const url = isAdminView && viewUserId 
            ? buildApiUrl(`/api/profile/profile?userId=${viewUserId}`)
            : buildApiUrl('/api/profile/profile');
          const res = await axios.get(url, { headers: authHeaders() });
          console.log('✅ General profile data:', res?.data);
          return res?.data || null;
        } catch (e2) {
          console.log('⚠️ General profile failed, trying auth/me...');
          // Fallback to /api/auth/me to at least get basic fields
          try {
            const url = isAdminView && viewUserId 
              ? buildApiUrl(`/api/auth/me?userId=${viewUserId}`)
              : buildApiUrl('/api/auth/me');
            const res = await axios.get(url, { headers: authHeaders() });
            console.log('✅ Auth/me data:', res?.data);
            return res?.data || null;
          } catch {
            console.log('❌ All profile endpoints failed');
            return null;
          }
        }
      }
    };

    const loadDashboardData = async () => {
      try {
        setLoading(true);
        console.log('🔍 Loading dashboard data...');
        console.log('🔑 Auth headers:', authHeaders());
        console.log('👤 Current user:', user);
        console.log('🔐 Is authenticated:', !!user);
        
        // Test authentication with a simple API call
        try {
          const testRes = await axios.get(buildApiUrl('/api/auth/me'), { headers: authHeaders() });
          console.log('✅ Auth test successful:', testRes.data);
        } catch (err) {
          console.log('❌ Auth test failed:', err.response?.status, err.message);
        }

        // Fetch in parallel with better error handling
        // NOTE: Order matters! Must match destructuring order exactly
        const [
          profileData,
          applicationsRes,
          notificationsRes,
          unreadNotifRes,
          unreadMsgRes,
          recommendedRes,
          careerResourcesRes,  // Position 6: Career resources API
          allJobsRes          // Position 7: All jobs API
        ] = await Promise.allSettled([
          fetchProfile(),  // 0
          // Applications - include userId if admin view
          axios.get(
            buildApiUrl(isAdminView && viewUserId 
              ? `/api/applications/my-applications?userId=${viewUserId}`
              : '/api/applications/my-applications'
            ), 
            { headers: authHeaders() }
          ),  // 1
          // Notifications list & counts - skip for admin view
          isAdminView ? Promise.resolve({ notifications: [] }) : notificationApi.getNotifications(),  // 2
          isAdminView ? Promise.resolve({ unread_count: 0 }) : notificationApi.getUnreadCount(),  // 3
          isAdminView ? Promise.resolve({ unread_count: 0 }) : messageApi.getUnreadCount(),  // 4
          // Recommended jobs for current user - include userId if admin view
          axios.get(
            buildApiUrl(isAdminView && viewUserId 
              ? `/api/jobs/recommended?userId=${viewUserId}`
              : '/api/jobs/recommended'
            ), 
            { headers: authHeaders() }
          ),  // 5
          // Career resources - personalized based on user profile
          axios.get(
            buildApiUrl('/api/jobseeker/career-resources'),
            { headers: authHeaders() }
          ),  // 6
          // All jobs for browse section - check for search parameters from navigation
          (() => {
            const searchParams = location.state || {};
            const query = searchParams.query || '';
            const locationParam = searchParams.location || '';
            const category = searchParams.category || '';
            
            // Build query string if search parameters exist
            const params = new URLSearchParams();
            if (query) params.append('query', query);
            if (locationParam) params.append('location', locationParam);
            if (category) params.append('category', category);
            
            const queryString = params.toString();
            const url = queryString 
              ? buildApiUrl(`/api/jobs/get_jobs?${queryString}`)
              : buildApiUrl('/api/jobs/get_jobs');
            
            console.log('🔍 Fetching all jobs from URL:', url);
            const axiosPromise = axios.get(url, { headers: authHeaders() });
            // Add logging to see what we get back
            axiosPromise.catch(err => {
              console.error('❌ allJobs axios error:', err.message);
            });
            return axiosPromise;
          })()  // 7
        ]);

        // Extract data from Promise.allSettled results
        console.log('🔍 Promise.allSettled completed. Checking responses...');
        console.log('🔍 allJobsRes status:', allJobsRes.status, 'URL should be /api/jobs/get_jobs');
        console.log('🔍 careerResourcesRes status:', careerResourcesRes.status, 'URL should be /api/jobseeker/career-resources');
        
        const profile = profileData.status === 'fulfilled' ? profileData.value : null;
        
        // Debug applications response
        console.log('🔍 Applications response status:', applicationsRes.status);
        console.log('🔍 Applications response value:', applicationsRes.value);
        if (applicationsRes.status === 'fulfilled' && applicationsRes.value) {
          console.log('🔍 Applications response.data:', applicationsRes.value.data);
          console.log('🔍 Applications response.data type:', typeof applicationsRes.value.data);
          console.log('🔍 Applications response.data isArray:', Array.isArray(applicationsRes.value.data));
          if (applicationsRes.value.data && Array.isArray(applicationsRes.value.data)) {
            console.log('🔍 Applications count:', applicationsRes.value.data.length);
            console.log('🔍 First application:', applicationsRes.value.data[0]);
          }
        } else if (applicationsRes.status === 'rejected') {
          console.error('❌ Applications request failed:', applicationsRes.reason);
          console.error('❌ Applications error details:', applicationsRes.reason?.response?.data);
        }
        
        // Handle different response structures
        let applications = [];
        if (applicationsRes.status === 'fulfilled' && applicationsRes.value) {
          // Axios response: applicationsRes.value is the axios response object, data is in .data
          if (applicationsRes.value.data !== undefined) {
            applications = Array.isArray(applicationsRes.value.data) 
              ? applicationsRes.value.data 
              : [];
          } 
          // Fallback: if value is already an array (shouldn't happen with axios)
          else if (Array.isArray(applicationsRes.value)) {
            applications = applicationsRes.value;
          }
        }
        
        console.log('📋 Extracted applications:', applications.length, 'apps:', applications);
        
        const notifications = notificationsRes.status === 'fulfilled' ? (notificationsRes.value?.notifications || []) : [];
        const unreadNotifCount = unreadNotifRes.status === 'fulfilled' ? (unreadNotifRes.value?.unread_count || 0) : 0;
        const unreadMsgCount = unreadMsgRes.status === 'fulfilled' ? (unreadMsgRes.value?.unread_count || 0) : 0;
        
        // Handle recommendedJobs with robust array checking
        let recommendedJobs = [];
        if (recommendedRes.status === 'fulfilled' && recommendedRes.value) {
          recommendedJobs = Array.isArray(recommendedRes.value?.data) 
            ? recommendedRes.value.data 
            : [];
        }
        
        // Handle allJobs with robust array checking
        console.log('📋 allJobsRes status:', allJobsRes.status);
        let allJobs = [];
        
        if (allJobsRes.status === 'fulfilled' && allJobsRes.value) {
          const axiosResponse = allJobsRes.value;
          console.log('📋 allJobsRes.value exists, is axios response:', axiosResponse && typeof axiosResponse === 'object');
          console.log('📋 allJobsRes.value keys:', axiosResponse ? Object.keys(axiosResponse) : 'no keys');
          console.log('📋 allJobsRes.value.config.url:', axiosResponse?.config?.url, 'should contain /api/jobs/get_jobs');
          
          // Axios response structure: response.data contains the actual response body
          const responseData = axiosResponse.data;
          console.log('📋 responseData:', responseData);
          console.log('📋 responseData type:', typeof responseData);
          console.log('📋 responseData isArray:', Array.isArray(responseData));
          console.log('📋 responseData length:', Array.isArray(responseData) ? responseData.length : 'N/A');
          
          // Check if we got the wrong response (career resources instead of jobs)
          if (responseData && typeof responseData === 'object' && !Array.isArray(responseData) && responseData.resources) {
            console.error('❌ ERROR: Got career resources response instead of jobs! Response has "resources" key');
          }
          
          if (Array.isArray(responseData)) {
            allJobs = responseData;
            console.log('✅✅✅ allJobs extracted from response.data, count:', allJobs.length);
            if (allJobs.length > 0) {
              console.log('✅ First job sample:', JSON.stringify(allJobs[0]).substring(0, 200));
            }
          } else if (responseData && typeof responseData === 'object' && Array.isArray(responseData.jobs)) {
            // Some APIs might wrap in {jobs: [...]}
            allJobs = responseData.jobs;
            console.log('✅ allJobs extracted from response.data.jobs, count:', allJobs.length);
          } else if (responseData && typeof responseData === 'object') {
            console.warn('⚠️ allJobs response.data is an object but not array. Keys:', Object.keys(responseData));
            console.warn('⚠️ Full responseData (first 1000 chars):', JSON.stringify(responseData).substring(0, 1000));
            allJobs = [];
          } else {
            console.warn('⚠️ allJobs response.data is not an array or object. Value:', responseData, 'type:', typeof responseData);
            allJobs = [];
          }
        } else if (allJobsRes.status === 'rejected') {
          console.error('❌ allJobs request failed:', allJobsRes.reason);
          console.error('❌ allJobs error details:', allJobsRes.reason?.response?.data);
          console.error('❌ allJobs error status:', allJobsRes.reason?.response?.status);
          allJobs = [];
        } else {
          console.warn('⚠️ allJobsRes status is not fulfilled:', allJobsRes.status, 'value:', allJobsRes.value);
        }
        
        console.log('📋 Final extracted allJobs:', allJobs.length, 'isArray:', Array.isArray(allJobs));
        if (allJobs.length > 0) {
          console.log('📋 Sample job:', allJobs[0]);
        }
        
        // Handle careerResources with robust array checking
        let careerResources = [];
        if (careerResourcesRes.status === 'fulfilled' && careerResourcesRes.value) {
          const resourcesData = careerResourcesRes.value?.data?.resources || careerResourcesRes.value?.data;
          careerResources = Array.isArray(resourcesData) ? resourcesData : [];
        }

        // Extract applied job IDs from applications
        const appliedIds = new Set();
        if (Array.isArray(applications)) {
        applications.forEach(app => {
          const jobId = app.job_id || app.jobId || app.job;
          if (jobId) {
            appliedIds.add(String(jobId));
          }
        });
        }
        setAppliedJobIds(appliedIds);

        console.log('📊 Dashboard data loaded:', {
          profile: !!profile,
          applications: Array.isArray(applications) ? applications.length : 0,
          notifications: Array.isArray(notifications) ? notifications.length : 0,
          unreadNotifCount,
          unreadMsgCount,
          recommendedJobs: Array.isArray(recommendedJobs) ? recommendedJobs.length : 0,
          allJobs: Array.isArray(allJobs) ? allJobs.length : 0
        });

        // DISABLED: Daily login bonus feature
        // try {
        //   console.log('🔍 Checking daily login bonus...');
        //   const dailyBonusRes = await axios.post(
        //     buildApiUrl('/api/coins/daily-login'),
        //     {},
        //     { headers: authHeaders() }
        //   );
        //   
        //   console.log('🔍 Daily bonus response:', dailyBonusRes.data);
        //   
        //   if (dailyBonusRes.data.coins_awarded) {
        //     console.log(`🪙 Daily login bonus awarded: ${dailyBonusRes.data.coins_amount} coins`);
        //     // Show alert to user
        //     alert(`🎉 Daily Login Bonus!\n\nYou earned ${dailyBonusRes.data.coins_amount} Akshar Coins for logging in today!`);
        //     
        //     // Also try to show notification if available
        //     if (window.notificationApi && window.notificationApi.create) {
        //       await window.notificationApi.create({
        //         title: 'Daily Login Bonus!',
        //         message: `You earned ${dailyBonusRes.data.coins_amount} Akshar Coins for logging in today! 🎉`,
        //         type: 'coin_award'
        //       });
        //     }
        //   } else if (dailyBonusRes.data.already_awarded) {
        //     console.log('ℹ️ Daily bonus already claimed today');
        //   }
        // } catch (dailyBonusError) {
        //   console.error('❌ Daily bonus check failed:', dailyBonusError);
        //   console.error('❌ Error details:', dailyBonusError.response?.data);
        //   console.error('❌ Status:', dailyBonusError.response?.status);
        //   // Don't fail dashboard load if daily bonus fails
        // }

        // Use only real data from API
        // Ensure allJobs and recommendedJobs are arrays before spreading
        const safeAllJobs = Array.isArray(allJobs) ? allJobs : [];
        const safeRecommendedJobs = Array.isArray(recommendedJobs) ? recommendedJobs : [];
        
        console.log('🔍 Before sorting - allJobs type:', typeof allJobs, 'isArray:', Array.isArray(allJobs), 'length:', safeAllJobs.length);
        console.log('🔍 Before sorting - recommendedJobs type:', typeof recommendedJobs, 'isArray:', Array.isArray(recommendedJobs), 'length:', safeRecommendedJobs.length);
        
        // Sort all jobs by date (most recent first)
        let finalAllJobs = [...safeAllJobs].sort((a, b) => {
          // Try multiple possible date fields (same as filter sort logic)
          const aPosted = a.posted_date || a.postedDate || a.postedAt || a.created_at || a.createdAt || a.datePosted || a.date_posted;
          const bPosted = b.posted_date || b.postedDate || b.postedAt || b.created_at || b.createdAt || b.datePosted || b.date_posted;
          
          // Handle missing dates - put them at the end
          if (!aPosted && !bPosted) return 0;
          if (!aPosted) return 1; // a goes to end
          if (!bPosted) return -1; // b goes to end
          
          const dateA = new Date(aPosted);
          const dateB = new Date(bPosted);
          
          // Handle invalid dates - put them at the end
          const aValid = !isNaN(dateA.getTime());
          const bValid = !isNaN(dateB.getTime());
          
          if (!aValid && !bValid) return 0;
          if (!aValid) return 1; // a goes to end
          if (!bValid) return -1; // b goes to end
          
          return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
        });
        
        // Sort recommended jobs by date (most recent first)
        let finalRecommendedJobs = [...safeRecommendedJobs].sort((a, b) => {
          // Try multiple possible date fields (same as filter sort logic)
          const aPosted = a.posted_date || a.postedDate || a.postedAt || a.created_at || a.createdAt || a.datePosted || a.date_posted;
          const bPosted = b.posted_date || b.postedDate || b.postedAt || b.created_at || b.createdAt || b.datePosted || b.date_posted;
          
          // Handle missing dates - put them at the end
          if (!aPosted && !bPosted) return 0;
          if (!aPosted) return 1; // a goes to end
          if (!bPosted) return -1; // b goes to end
          
          const dateA = new Date(aPosted);
          const dateB = new Date(bPosted);
          
          // Handle invalid dates - put them at the end
          const aValid = !isNaN(dateA.getTime());
          const bValid = !isNaN(dateB.getTime());
          
          if (!aValid && !bValid) return 0;
          if (!aValid) return 1; // a goes to end
          if (!bValid) return -1; // b goes to end
          
          return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
        });
        
        // Ensure applications is an array before using
        const safeApplications = Array.isArray(applications) ? applications : [];
        let finalApplications = safeApplications;
        
        console.log(`📅 Sorted ${finalAllJobs.length} jobs by date (most recent first)`);
        console.log(`📋 Final applications before stats calculation:`, finalApplications.length);
        console.log('🔍 CRITICAL CHECK - finalAllJobs before setting dashboardData:', finalAllJobs.length, 'isArray:', Array.isArray(finalAllJobs));
        if (finalAllJobs.length > 0) {
          console.log('🔍 CRITICAL CHECK - First job in finalAllJobs:', finalAllJobs[0]);
        } else {
          console.warn('🔍 CRITICAL CHECK - finalAllJobs is EMPTY!');
          console.warn('🔍 CRITICAL CHECK - allJobs variable:', allJobs.length);
          console.warn('🔍 CRITICAL CHECK - safeAllJobs:', safeAllJobs.length);
        }

        const initialSaved = loadSavedJobs();

        // Derive stats and interviews list (applications with interview details set by employer)
        const applicationsSent = finalApplications.length;
        const interviewsList = (finalApplications || []).filter(
          a => (a.interview_date || a.interviewDate || (a.status || '').toLowerCase().includes('interview'))
        );
        const interviewsScheduled = interviewsList.length;
        const savedJobs = initialSaved.size;
        
        console.log('📊 Calculated stats:', {
          applicationsSent,
          interviewsScheduled,
          savedJobs,
          profileViews: dashboardData.stats.profileViews
        });

        // Use backend's unified profile completion calculation
        // Backend returns profileCompletion from utils/profile_progress.py
        // This ensures Dashboard and MyProfile show the SAME percentage
        let profileCompletion = 0;
        let isDraft = false;
        let showAsDraft = false;  // New: Controls when to show "Draft" vs percentage
        
        if (profile) {
          // Use ONLY backend's profileCompletion - same source as MyProfile page
          // Do NOT use Math.max with a local calculation; that caused dashboard to show
          // 100% while MyProfile correctly showed 95% (different field sets)
          profileCompletion = profile.profileCompletion ?? profile.profileStatus?.completionPercentage ?? 0;
          
          // Check if profile is marked as draft
          isDraft = profile.isDraft === true;
          
          // SMART LOGIC: Show "Draft" only if:
          // 1. Explicitly saved as draft AND
          // 2. Has minimal data (< 20% completion)
          // Otherwise, show the actual percentage even if isDraft is true
          showAsDraft = isDraft && profileCompletion < 20;
          
          console.log('📊 Dashboard - Profile Completion (from backend, same as MyProfile):', profileCompletion, '%');
          console.log('📝 Dashboard - Is Draft:', isDraft);
          console.log('🎯 Dashboard - Show as Draft:', showAsDraft);
        } else {
          profileCompletion = 0;
          showAsDraft = false;
        }

        setUnreadNotifications(Number(unreadNotifCount) || 0);
        setUnreadMessages(Number(unreadMsgCount) || 0);
        setSavedJobIds(initialSaved);

        // CRITICAL: Log before setting dashboardData
        console.log('🔍 ABOUT TO SET dashboardData - finalAllJobs:', finalAllJobs.length, 'type:', typeof finalAllJobs, 'isArray:', Array.isArray(finalAllJobs));
        const allJobsToSet = Array.isArray(finalAllJobs) ? finalAllJobs.slice(0, 50) : [];
        console.log('🔍 allJobsToSet length:', allJobsToSet.length);
        
        setDashboardData(prev => {
          const newDashboardData = {
          ...prev,
          stats: {
            applicationsSent,
            interviewsScheduled,
            profileViews: prev.stats.profileViews,
            savedJobs
          },
            profileCompletion,
            isDraft,
            showAsDraft,  // New: Controls display logic
            recentApplications: Array.isArray(finalApplications) ? finalApplications.slice(0, 5) : [],
            recommendedJobs: Array.isArray(finalRecommendedJobs) ? finalRecommendedJobs.slice(0, 6) : [],
            allJobs: allJobsToSet, // show more for saved matching
          careerResources: careerResources, // AI-recommended career resources
          interviews: interviewsList, // interview details set by employer for Interviews page
          // Persist full profile for My Profile rendering
          user: {
            // prefer explicit names, fallback to fullName split
            firstName: user?.firstName || profile?.firstName || (profile?.fullName ? profile.fullName.split(' ')[0] : 'User'),
            lastName: user?.lastName || profile?.lastName || (profile?.fullName ? profile.fullName.split(' ').slice(1).join(' ') : ''),
            // Include professional title directly for easy access
            professionalTitle: profile?.professionalTitle,
            currentJobTitle: profile?.currentJobTitle,
            fullProfile: profile || {}
          }
          };
          
          console.log('✅ Setting dashboard data with stats:', newDashboardData.stats);
          console.log('✅ Recent applications count:', newDashboardData.recentApplications.length);
          console.log('✅ Setting dashboardData - allJobs count:', newDashboardData.allJobs.length, 'isArray:', Array.isArray(newDashboardData.allJobs));
          console.log('✅ Setting dashboardData - recommendedJobs count:', newDashboardData.recommendedJobs.length);
          console.log('✅ Setting dashboardData - finalAllJobs count:', finalAllJobs.length);
          if (newDashboardData.allJobs.length > 0) {
            console.log('✅ First job in allJobs:', newDashboardData.allJobs[0]);
          } else {
            console.warn('⚠️ WARNING: allJobs is empty in dashboardData!');
            console.warn('⚠️ finalAllJobs length:', finalAllJobs.length);
            console.warn('⚠️ safeAllJobs length:', safeAllJobs.length);
            console.warn('⚠️ allJobs variable length:', allJobs.length);
          }
          
          return newDashboardData;
        });
      } catch (error) {
        console.error('❌ Error loading dashboard data:', error);
        console.error('❌ Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          stack: error.stack
        });
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [viewUserId, isAdminView]); // Reload when viewUserId changes (for admin view mode)

  // Animate progress indicator when percentage changes
  useEffect(() => {
    if (dashboardData.profileCompletion !== undefined) {
      // Force a re-render to trigger the animation
      const indicator = document.querySelector('.progress-indicator');
      if (indicator) {
        indicator.style.transition = 'none';
        // Trigger reflow to reset the transition
        void indicator.offsetHeight;
        indicator.style.transition = 'left 2s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      }
    }
  }, [dashboardData.profileCompletion]);

  // Initialize profile form when profile loads
  useEffect(() => {
    const p = dashboardData.user?.fullProfile || {};
    if (p || user) {
      const formatLocation = (loc) => {
        if (!loc) return '';
        if (typeof loc === 'string') return loc;
        const parts = [loc.city, loc.state, loc.country].filter(Boolean);
        return parts.join(', ');
      };
      const formatAvailability = (av) => {
        if (!av) return '';
        if (typeof av === 'string' || typeof av === 'number') return String(av);
        try {
          if (Array.isArray(av)) {
            return av.map(x => (typeof x === 'string' ? x : (x?.label || x?.name || x?.value || ''))).filter(Boolean).join(', ');
          }
          // object - pick the first meaningful text field
          const candidates = ['label', 'name', 'value', 'status', 'text', 'display'];
          for (const key of candidates) {
            if (av && typeof av[key] === 'string' && av[key]) return av[key];
          }
          // fallback: join string values
          const vals = Object.values(av).filter(v => typeof v === 'string');
          if (vals.length) return vals.join(' ');
          return '';
        } catch {
          return '';
        }
      };

      setProfileForm(prev => ({
        ...prev,
        firstName: user?.firstName || p.firstName || '',
        lastName: user?.lastName || p.lastName || '',
        email: user?.email || p.email || localStorage.getItem('userEmail') || '',
        phoneNumber: p.phoneNumber || p.phone || '',
        location: formatLocation(p.location),
        professionalTitle: p.professionalTitle || '',
        professionalSummary: p.professionalSummary || p.summary || '',
        yearsOfExperience: p.yearsOfExperience || p.yearsExperience || '',
        skills: Array.isArray(p.skills) ? p.skills.join(', ') : (p.skills || ''),
        tools: Array.isArray(p.tools) ? p.tools.join(', ') : (p.tools || ''),
        expectedSalary: p.expectedSalary || '',
        currency: p.currency || '',
        availability: formatAvailability(p.availability),
        careerLevel: p.careerLevel || '',
        industry: p.industry || '',
        jobTypePreference: p.jobTypePreference || p.job_type || ''
      }));
      setEducation(Array.isArray(p.education) ? p.education : []);
      setExperience(Array.isArray(p.experience) ? p.experience : (Array.isArray(p.workExperience) ? p.workExperience : []));
      setLanguages(Array.isArray(p.languages) ? p.languages.map(l => (typeof l === 'string' ? { language: l, proficiency: '' } : l)) : []);
      setCertifications(Array.isArray(p.certifications) ? p.certifications : []);
      setProfessionalLinks(Array.isArray(p.professionalLinks) ? p.professionalLinks : []);
      setReferences(Array.isArray(p.references) ? p.references : []);
    }
  }, [dashboardData.user, user]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileForm(prev => ({ ...prev, [name]: value }));
  };

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      setProfileMessage('');
      const token = localStorage.getItem('token');
      const payload = {
        ...profileForm,
        skills: profileForm.skills
          ? profileForm.skills.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        tools: profileForm.tools
          ? profileForm.tools.split(',').map(s => s.trim()).filter(Boolean)
          : [],
        education,
        experience,
        languages,
        certifications,
        professionalLinks,
        references,
        profileCompleted: true
      };
      await axios.put(buildApiUrl('/api/profile/profile'), payload, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      // Re-fetch profile to reflect latest values in the UI
      const updated = await axios.get(buildApiUrl('/api/profile/profile'), { headers: authHeaders() });
      if (updated?.data) {
        setDashboardData(prev => ({ ...prev, user: { ...prev.user, fullProfile: updated.data } }));
      }
      setProfileMessage('Profile saved successfully.');
      setEditMode(false);
    } catch (err) {
      console.error('Save profile error:', err);
      setProfileMessage('Failed to save profile.');
    } finally {
      setSavingProfile(false);
    }
  };
  const cancelEdit = () => {
    // restore from latest fullProfile
    const p = dashboardData.user?.fullProfile || {};
    setEditMode(false);
    // trigger form re-init by reusing existing init effect
    setDashboardData(d => ({ ...d }));
  };

  // Utility: quickly fill form with sample data and save to verify display
  const autoFillSampleProfile = async () => {
    setEducation([{ school: 'Nairobi University', degree: 'B.Sc.', field: 'Computer Science', startDate: '2016', endDate: '2020' }]);
    setExperience([{ company: 'TechCorp Kenya', title: 'Senior Software Engineer', startDate: '2021-01', endDate: 'Present', description: 'Lead backend services and mentor team.' }]);
    setLanguages([{ language: 'English', proficiency: 'Fluent' }, { language: 'Swahili', proficiency: 'Fluent' }]);
    setCertifications([{ name: 'AWS Solutions Architect', issuer: 'Amazon', date: '2023' }]);
    setProfessionalLinks([{ label: 'LinkedIn', url: 'https://www.linkedin.com/in/rahul-shah' }]);
    setReferences([{ name: 'Jane Recruiter', contact: 'jane@techcorp.co.ke', relation: 'Manager' }]);
    setProfileForm(prev => ({
      ...prev,
      professionalTitle: 'Senior Software Engineer',
      professionalSummary: 'Experienced engineer with a focus on scalable APIs, mentoring, and DevOps.',
      yearsOfExperience: '6',
      skills: 'Python, Flask, React, MongoDB, Docker, AWS',
      tools: 'GitHub, Jira, Jenkins',
      expectedSalary: 'KSh 200,000',
      currency: 'KSh',
      careerLevel: 'Senior',
      availability: 'Immediately',
      location: prev.location || 'Nairobi, Kenya'
    }));
    // Save after state updates flush
    setTimeout(() => { saveProfile(); }, 100);
  };

  // Handle job application
  const handleApplyToJob = async (job) => {
    // Check if profile is at least 75% complete
    const profileCompletion = dashboardData.profileCompletion || 0;
    if (profileCompletion < 75) {
      const confirmMessage = `⚠️ Profile Completion Required\n\nYour profile is currently ${profileCompletion}% complete.\n\nYou must complete at least 75% of your profile to apply for jobs.\n\nWould you like to go to your profile page now to complete it?`;
      if (window.confirm(confirmMessage)) {
        navigate('/profile');
      }
      return;
    }

    // Prevent duplicate applications on the client side as an extra safety check
    const jobIdStr = String(job._id || job.id);
    if (appliedJobIds.has(jobIdStr)) {
      alert('You have already applied for this job.');
      return;
    }

    try {
      const response = await axios.post(
        buildApiUrl('/api/applications/apply'),
        {
          job_id: job._id,
          cover_letter: `I am very interested in the ${job.job_title} position at ${job.company_name}. I believe my skills and experience make me a great fit for this role.`,
          resume_url: '', // TODO: Get from user profile
          additional_info: ''
        },
        { headers: authHeaders() }
      );

      if (response.status === 200 || response.status === 201) {
        alert('Application submitted successfully!');
        
        // Immediately mark job as applied in UI (optimistic update)
        const jobIdStr = String(job._id || job.id);
        setAppliedJobIds(prev => new Set([...prev, jobIdStr]));
        
        // Refresh applications in background - don't block user
        axios.get(buildApiUrl('/api/applications/my-applications'), { headers: authHeaders() })
          .then((applicationsRes) => {
            const applications = Array.isArray(applicationsRes?.data) ? applicationsRes.data : [];
            const appliedIds = new Set();
            if (Array.isArray(applications)) {
              applications.forEach(app => {
                const appJobId = app.job_id || app.jobId || app.job;
                if (appJobId) appliedIds.add(String(appJobId));
              });
            }
            setAppliedJobIds(appliedIds);
            setDashboardData(prev => ({
              ...prev,
              recentApplications: Array.isArray(applications) ? applications.slice(0, 5) : [],
              stats: { ...prev.stats, applicationsSent: Array.isArray(applications) ? applications.length : 0 }
            }));
          })
          .catch(() => { /* Non-critical: list will refresh on next page load */ });
        
        return response.data;
      }
    } catch (error) {
      console.error('Error applying to job:', error);
      const backendMessage = error?.response?.data?.error || '';

      // If backend says we've already applied, show a friendly message and
      // make sure the UI reflects the applied state.
      if (backendMessage.toLowerCase().includes('already applied')) {
        const jobIdStr = String(job._id || job.id);
        setAppliedJobIds(prev => new Set([...prev, jobIdStr]));
        alert('You have already applied for this job.');
        return;
      }

      alert('Failed to apply to job. Please try again.');
      throw error;
    }
  };

  const viewDetails = (job) => {
    openJobDetailsTab(job);
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Using imported StatCard component from components folder

  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'jobs': 'Browse Jobs',
      'applications': 'Application Tracker',
      'saved': 'Saved Jobs',
      'interviews': 'Interviews',
      'recommended': 'Recommended',
      'messages': 'Messages',
      'profile': 'My Profile',
      'resume': 'Resume/CV',
      'resources': 'Career Resources',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  const userFirstName = (
    user?.firstName
    || user?.first_name
    || dashboardData.user?.firstName
    || dashboardData.user?.first_name
    || dashboardData.user?.fullProfile?.firstName
    || dashboardData.user?.fullProfile?.first_name
    || 'there'
  );
  const profileCompletion = Math.max(0, Math.min(100, dashboardData.profileCompletion || 0));
  const upcomingInterviews = Array.isArray(dashboardData.interviews)
    ? dashboardData.interviews.slice(0, 2)
    : [];

  if (loading) {
    return (
      <div className="jobseeker-dashboard-container">
        <div className="loading-container">
          <ThemedLoadingSpinner theme="jobseeker" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {isMobileView ? (
        <JobSeekerDashboardMobileHeader
          onOpenMessages={() => {
            setShowMobileSidebar(false);
            setActiveSection('messages');
          }}
        />
      ) : (
        <EmployerRecruiterHeader userType="jobSeeker" />
      )}

      {isAdminView && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '16px 24px',
          margin: '20px auto',
          maxWidth: '1200px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: '20px' }} />
            <div>
              <strong>Admin View Mode</strong>
              <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                Viewing dashboard for user: {viewUserId}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin?view=profile-browser')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Back to Profile Browser
          </button>
        </div>
      )}

      <div className="dashboard-container jobseeker-dashboard-container" style={{ marginTop: '0' }}>
        <div
          className={`sidebar-overlay ${showMobileSidebar ? 'show' : ''}`}
          onClick={() => setShowMobileSidebar(false)}
          aria-hidden="true"
        />

        {/* Sidebar */}
        <div 
          className={`sidebar${isMobileView ? ' js-mobile-sidebar' : ''}${showMobileSidebar ? ' show sidebar-open' : ''}`}
          style={isMobileView ? { paddingTop: '10px' } : { top: '80px', height: 'calc(100vh - 80px)', paddingTop: '20px' }}
          onClick={(e) => {
            if (isMobileView && e.target.closest('.nav-item')) {
              setShowMobileSidebar(false);
            }
          }}
        >
        <div className="nav-menu">
          {navigationItems.map((item, index) => (
            <div key={item.id}>
              {index === 2 && <div className="nav-separator"></div>}
              <div 
                className={`nav-item ${activeSection === item.section ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && (
                  <span className={`badge ${item.badge === 'NEW' ? 'new' : ''}`}>
                    {item.badge}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div
          className="sidebar-card"
          style={{
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.2)',
            marginBottom: 'max(12px, env(safe-area-inset-bottom, 0px))'
          }}
        >
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
            Profile Complete
          </div>
          <div style={{ fontSize: '28px', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Content Area */}
        <div className={`content-area ${activeSection === 'settings' ? 'settings-active' : ''}`} ref={sectionRefs.current.dashboardTop}>
          {/* Show different content based on active section */}
          {activeSection === 'dashboard' && (
            <div className="js-dashboard-home" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              {!isMobileView && (
                <section className="js-welcome-strip" aria-label="Dashboard overview">
                  <div className="js-welcome-strip-content">
                    <div className="js-welcome-strip-text">
                      <h2 className="js-welcome-title">
                        {getDashboardGreeting()}, {userFirstName}
                      </h2>
                      <p className="js-welcome-subtitle">
                        {profileCompletion < 80
                          ? 'Complete your profile to unlock better job matches and employer visibility.'
                          : 'Track your applications and discover roles matched to your profile.'}
                      </p>
                    </div>
                    <div className="js-welcome-actions">
                      <button
                        type="button"
                        className="btn btn-primary js-welcome-action-btn"
                        onClick={() => setActiveSection('jobs')}
                      >
                        <FontAwesomeIcon icon={faSearch} />
                        Browse Jobs
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary js-welcome-action-btn"
                        onClick={() => navigate('/profile')}
                      >
                        <FontAwesomeIcon icon={faUser} />
                        {profileCompletion < 80 ? 'Complete Profile' : 'Update Profile'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary js-welcome-action-btn"
                        onClick={() => setActiveSection('applications')}
                      >
                        <FontAwesomeIcon icon={faFileAlt} />
                        My Applications
                      </button>
                    </div>
                  </div>
                </section>
              )}

              <div className="stats-grid" ref={sectionRefs.current.statsGrid}>
                <StatCard
                  title="Applications Sent"
                  value={dashboardData.stats.applicationsSent}
                  showTrend={false}
                  icon={faPaperPlane}
                  iconColor="#f97316"
                />
                <StatCard
                  title="Interviews Scheduled"
                  value={dashboardData.stats.interviewsScheduled}
                  showTrend={false}
                  icon={faCalendarCheck}
                  iconColor="#10b981"
                />
                <StatCard
                  title="Profile Views"
                  value={dashboardData.stats.profileViews}
                  showTrend={false}
                  icon={faEye}
                  iconColor="#3b82f6"
                />
                <StatCard
                  title="Saved Jobs"
                  value={dashboardData.stats.savedJobs}
                  showTrend={false}
                  icon={faBookmark}
                  iconColor="#8b5cf6"
                />
              </div>

              <div className="js-dashboard-layout">
                <div className="js-dashboard-main">
                  <div className="content-grid">
                    <div className="recommended-section" ref={sectionRefs.current.recommended}>
                      <div className="recommended-header">
                        <h3 className="recommended-title">Recommended for You</h3>
                        <button type="button" className="btn-view-all" onClick={() => setActiveSection('recommended')}>View All</button>
                      </div>
                      <div className="card-content">
                        {dashboardData.recommendedJobs && dashboardData.recommendedJobs.length > 0 ? (
                          isMobileView ? (
                            <ul className="list">
                              {dashboardData.recommendedJobs.slice(0, 4).map((job, idx) => (
                                <li key={idx} className="list-item recommended-job-item" onClick={() => viewDetails(job)}>
                                  <div className="job-info">
                                    <div className="job-card-top-row">
                                      <div className="job-title-row">
                                        <div className="list-title">{sanitizeJobTitle(job.job_title || job.title)}</div>
                                        {job.match_score > 0 && (
                                          <span className="job-match-badge" style={{
                                            background: job.match_score >= 70 ? '#10b981' : job.match_score >= 40 ? '#f59e0b' : '#6b7280',
                                          }}>
                                            {job.match_score}% Match
                                          </span>
                                        )}
                                      </div>
                                      <div className="job-actions">
                                        <button type="button" className="btn btn-primary btn-sm job-apply-btn" onClick={(e) => {
                                          e.stopPropagation();
                                          handleApplyToJob(job);
                                        }}>
                                          Apply
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-sm job-save-btn" onClick={(e) => {
                                          e.stopPropagation();
                                          toggleSaveJob(job._id);
                                        }}>
                                          <FontAwesomeIcon icon={faBookmark} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="list-subtitle">{job.company_name || job.companyName || job.company || '—'}</div>
                                    <div className="job-meta">
                                      <span className="job-location">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                                        {job.location || 'Remote'}
                                      </span>
                                      <span className="job-type">
                                        <FontAwesomeIcon icon={faBriefcase} />
                                        {job.job_type || 'Full-time'}
                                      </span>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="list dashboard-job-list">
                              {dashboardData.recommendedJobs.slice(0, 5).map((job, idx) => {
                                const jobId = String(job._id || job.id || idx);
                                return (
                                  <DashboardJobRow
                                    key={jobId}
                                    job={{
                                      ...job,
                                      displayTitle: sanitizeJobTitle(job.job_title || job.title),
                                    }}
                                    companyName={getDisplayCompanyName(job)}
                                    postedLabel={formatJobPostedRelative(job)}
                                    matchScore={getJobMatchScore(job)}
                                    isSaved={savedJobIds.has(job._id || job.id)}
                                    isApplied={appliedJobIds.has(jobId)}
                                    onApply={handleApplyToJob}
                                    onSave={toggleSaveJob}
                                    onViewDetails={viewDetails}
                                  />
                                );
                              })}
                            </ul>
                          )
                        ) : (dashboardData.profileCompletion >= 80) ? (
                          <div className="empty-state">
                            <FontAwesomeIcon icon={faStar} className="empty-icon" />
                            <h3>No AI Recommendations Yet</h3>
                            <p>We&apos;re working on finding personalized job recommendations for you. Check back soon!</p>
                          </div>
                        ) : (
                          <div className="empty-state">
                            <FontAwesomeIcon icon={faStar} className="empty-icon" />
                            <h3>No AI Recommendations Yet</h3>
                            <p>Complete your profile to get personalized job recommendations powered by AI.</p>
                            {!isMobileView && (
                              <button type="button" className="btn btn-primary" onClick={() => navigate('/profile')}>
                                Complete Profile
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="recommended-section" ref={sectionRefs.current.recentApplications}>
                      <div className="recommended-header">
                        <h3 className="recommended-title">Recent Applications</h3>
                        <button type="button" className="btn-view-all" onClick={() => setActiveSection('applications')}>View All</button>
                      </div>
                      <div className="card-content">
                        {dashboardData.recentApplications && dashboardData.recentApplications.length > 0 ? (
                          isMobileView ? (
                            <ul className="list">
                              {dashboardData.recentApplications.slice(0, 4).map((app, idx) => (
                                <li key={idx} className="list-item application-list-item">
                                  <div className="application-info">
                                    <div className="application-card-top-row">
                                      <div className="list-title">{sanitizeJobTitle(app.job_title)}</div>
                                      <div className="application-title-actions">
                                        <span className={`status-badge status-${(app.status || 'applied').toLowerCase().replace(' ', '-')}`}>
                                          {app.status || 'Applied'}
                                        </span>
                                        <button type="button" className="btn btn-secondary btn-sm application-view-btn" aria-label="View application">
                                          <FontAwesomeIcon icon={faEye} />
                                        </button>
                                      </div>
                                    </div>
                                    <div className="application-details-stack">
                                      <div className="list-subtitle">{app.company_name || ''}</div>
                                      <div className="recent-application-date">
                                        <FontAwesomeIcon icon={faCalendar} className="recent-application-date-icon" />
                                        <span className="recent-application-date-text">
                                          Applied {new Date(app.applied_date || app.applied_at || app.created_at || Date.now()).toLocaleDateString()}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <ul className="list dashboard-application-list">
                              {dashboardData.recentApplications.slice(0, 4).map((app, idx) => (
                                <DashboardApplicationRow
                                  key={app._id || app.id || idx}
                                  application={app}
                                  onView={() => setActiveSection('applications')}
                                />
                              ))}
                            </ul>
                          )
                        ) : (
                          <div className="empty-state">
                            <FontAwesomeIcon icon={faFileAlt} className="empty-icon" />
                            <h3>No Applications Yet</h3>
                            <p>Start applying to jobs to track your progress here.</p>
                            {!isMobileView && (
                              <button type="button" className="btn btn-primary" onClick={() => setActiveSection('jobs')}>
                                Browse Jobs
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {!isMobileView && (
                  <aside className="js-dashboard-rail" aria-label="Dashboard insights">
                    <div className="js-rail-card js-rail-profile">
                      <h3 className="js-rail-card-title">Profile Strength</h3>
                      <div className="js-rail-profile-ring" style={{ '--completion': `${profileCompletion}%` }}>
                        <span className="js-rail-profile-value">{profileCompletion}%</span>
                      </div>
                      <p className="js-rail-profile-copy">
                        {profileCompletion >= 80
                          ? 'Strong profile — employers can see your full experience.'
                          : 'Add skills, experience, and a resume to rank higher in searches.'}
                      </p>
                      <button type="button" className="js-rail-card-link" onClick={() => navigate('/profile')}>
                        {profileCompletion >= 80 ? 'View profile' : 'Improve profile'}
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>

                    <div className="js-rail-card js-rail-interviews">
                      <h3 className="js-rail-card-title">Upcoming Interviews</h3>
                      {upcomingInterviews.length > 0 ? (
                        <ul className="js-rail-interview-list">
                          {upcomingInterviews.map((interview, idx) => (
                            <li key={interview._id || interview.id || idx} className="js-rail-interview-item">
                              <strong>{sanitizeJobTitle(interview.job_title || interview.title || 'Interview')}</strong>
                              <span>{interview.company_name || interview.company || 'Employer'}</span>
                              <span className="js-rail-interview-date">
                                <FontAwesomeIcon icon={faCalendarCheck} />
                                {formatInterviewRailDate(interview)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="js-rail-empty">No interviews scheduled yet. Keep applying — your next opportunity is close.</p>
                      )}
                      <button type="button" className="js-rail-card-link" onClick={() => setActiveSection('interviews')}>
                        Open calendar
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  </aside>
                )}
              </div>
            </div>
          )}

          {/* Application Tracker Section */}
          {activeSection === 'applications' && (
            <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
            <ApplicationTrackerSection 
              ref={sectionRefs.current.applications}
              useCustomDropdown={!isMobileView}
            />
            </div>
          )}

          {/* Browse Jobs Section */}
          {activeSection === 'jobs' && (
            <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
            <BrowseJobsSection 
              jobs={Array.isArray(dashboardData.allJobs) ? dashboardData.allJobs : []}
              onApply={handleApplyToJob}
              onSave={toggleSaveJob}
              onViewDetails={viewDetails}
              savedJobIds={savedJobIds}
              appliedJobIds={appliedJobIds}
              useCustomDropdown={!isMobileView}
              initialSearch={(() => {
                const s = location.state || {};
                const q = (s.query || '').trim();
                const loc = (s.location || '').trim();
                return q || loc || '';
              })()}
            />
            </div>
          )}

          {/* Saved Jobs Section */}
          {activeSection === 'saved' && (
            <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
            <div className="card" ref={sectionRefs.current.saved}>
              <div className="card-header">
                <h3 className="card-title">Saved Jobs</h3>
              </div>
              <div className="card-content">
                {dashboardData.allJobs && dashboardData.allJobs.filter(j => savedJobIds.has(j._id)).length > 0 ? (
                  <div className="jobs-list">
                    {dashboardData.allJobs.filter(j => savedJobIds.has(j._id)).map((job, idx) => (
                      <JobCard
                        key={job._id || idx}
                        job={job}
                        onApply={handleApplyToJob}
                        onSave={toggleSaveJob}
                        onViewDetails={viewDetails}
                        isSaved={savedJobIds.has(job._id)}
                        isAlreadyApplied={appliedJobIds.has(String(job._id || job.id))}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faBookmark} className="empty-icon" />
                    <h3>No Saved Jobs</h3>
                    <p>Bookmark jobs to see them here.</p>
                  </div>
                )}
              </div>
              </div>
            </div>
          )}

          {/* Interview Calendar - view interviews set by employers (read-only) */}
          {activeSection === 'interviews' && (
            <div style={{ marginTop: '-1rem', paddingTop: '0' }} ref={sectionRefs.current.interviews}>
              <InterviewCalendar
                variant="jobseeker"
                isMobileView={isMobileView}
                useCustomDropdown={!isMobileView}
              />
            </div>
          )}

          {/* Recommended Section */}
          {activeSection === 'recommended' && (
            <RecommendedJobsSection
              recommendedJobs={dashboardData.recommendedJobs || []}
              profileCompletion={dashboardData.profileCompletion || 0}
                        onApply={handleApplyToJob}
                        onSave={toggleSaveJob}
                        onViewDetails={viewDetails}
              savedJobIds={savedJobIds}
              appliedJobIds={appliedJobIds}
              navigate={navigate}
              useCustomDropdown={!isMobileView}
              ref={sectionRefs.current.recommended}
            />
          )}

          {/* Messages Section */}
          {activeSection === 'messages' && (
            <div ref={sectionRefs.current.messages} style={{ width: '100%', marginLeft: '-20px', marginRight: '-20px', marginTop: '-1rem', paddingTop: '0' }}>
              <ProfessionalMessaging />
            </div>
          )}

          {/* My Profile Section - Now handled by dedicated /profile route */}
          {/* REMOVED: Old profile section to prevent conflicts with dedicated MyProfile page */}
          {false && (
            <div className="card" ref={sectionRefs.current.profile} style={elevatedCardStyle}>
              <div className="card-content" style={{padding:0}}>
                {/* Profile Header Card */}
                <div className="profile-header-card" style={profileHeaderStyle}>
                  <div style={{display:'flex', alignItems:'center', gap:'30px'}}>
                    <div className="profile-avatar-large">{(profileForm.firstName?.[0]||'U')}{(profileForm.lastName?.[0]||'')}</div>
                    <div style={{flex:1}}>
                      <h2 style={{marginBottom:'10px', fontSize:'28px'}}>{profileForm.firstName} {profileForm.lastName}</h2>
                      <p style={{fontSize:'18px', marginBottom:'10px', opacity:0.9}}>{profileForm.professionalTitle || '—'}</p>
                      <p style={{opacity:0.8}}><FontAwesomeIcon icon={faMapMarkerAlt}/> {profileForm.location || '—'}</p>
                    </div>
                    <div style={{display:'flex', gap:'8px'}}>
                      {!editMode && (
                        <button className="btn btn-primary btn-sm" onClick={()=>setEditMode(true)}>
                          <FontAwesomeIcon icon={faEdit}/> Edit Profile
                        </button>
                      )}
                      {editMode && (
                        <>
                          <button className="btn btn-success btn-sm" onClick={saveProfile} disabled={savingProfile}>
                            <FontAwesomeIcon icon={faSave}/> {savingProfile ? 'Saving...' : 'Save Changes'}
                          </button>
                          <button className="btn btn-secondary btn-sm" onClick={cancelEdit}>
                            <FontAwesomeIcon icon={faTimes}/> Cancel
                          </button>
                        </>
                      )}
                      <button className="btn btn-complete btn-sm" onClick={saveProfile} disabled={savingProfile}>
                        <FontAwesomeIcon icon={faCheckCircle}/> {savingProfile ? 'Saving...' : 'Complete Profile'}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={autoFillSampleProfile}>Fill Sample Data</button>
                    </div>
                  </div>
                </div>
                {/* Personal Information */}
                <div className="card profile-view-mode" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faUser}/> Personal Information</h3>
                  </div>
                  <div className="profile-info-grid">
                    <div className="form-group">
                      <label className="form-label">Full Name</label>
                      <input className="form-input" style={fieldViewModeStyle()} value={`${profileForm.firstName||''} ${profileForm.lastName||''}`.trim()} disabled={!editMode} onChange={(e)=>{
                        const [f,...r]=e.target.value.split(' '); setProfileForm(p=>({...p, firstName:f||'', lastName:r.join(' ')||''}))
                      }}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address</label>
                      <input className="form-input" style={fieldViewModeStyle()} value={profileForm.email||''} disabled/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input className="form-input" style={fieldViewModeStyle()} value={profileForm.phoneNumber||''} disabled={!editMode} onChange={handleProfileChange} name="phoneNumber"/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Location</label>
                      <input className="form-input" style={fieldViewModeStyle()} value={profileForm.location||''} disabled={!editMode} onChange={handleProfileChange} name="location"/>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Professional Summary</label>
                    <textarea className="form-textarea" style={fieldViewModeStyle()} value={profileForm.professionalSummary||''} disabled={!editMode} name="professionalSummary" onChange={handleProfileChange}/>
                  </div>
                </div>
                {/* Professional Details */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faBriefcase}/> Professional Details</h3>
                  </div>
                  <div className="profile-info-grid">
                    <div className="form-group">
                      <label className="form-label">Current Job Title</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="professionalTitle" value={profileForm.professionalTitle||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Years of Experience</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="yearsOfExperience" value={profileForm.yearsOfExperience||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Industry</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="industry" value={profileForm.industry||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Expected Salary</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="expectedSalary" value={profileForm.expectedSalary||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Job Type Preference</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="jobTypePreference" value={profileForm.jobTypePreference||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Availability</label>
                      <input className="form-input" style={fieldViewModeStyle()} name="availability" value={profileForm.availability||''} disabled={!editMode} onChange={handleProfileChange}/>
                    </div>
                  </div>
                </div>

                {/* Skills & Expertise */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faCode}/> Skills & Expertise</h3>
                  </div>
                  <div>
                    <div className="skill-category-label">Technical Skills</div>
                    <div>
                      {(profileForm.skills||'').split(',').filter(Boolean).map((s,i)=> (
                        <span key={i} className="skill-tag" style={pillTagStyle}>{s.trim()}</span>
                      ))}
                    </div>
                  </div>
                  <div style={{marginTop:'25px'}}>
                    <div style={{marginBottom:'15px', color:'#666', fontWeight:600}}>Tools</div>
                    <div>
                      {(profileForm.tools||'').split(',').filter(Boolean).map((s,i)=> (
                        <span key={i} className="skill-tag" style={pillTagStyle}>{s.trim()}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Work Experience */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faBuilding}/> Work Experience</h3>
                  </div>
                  {(experience||[]).length === 0 && <div className="empty-text">No experience added</div>}
                  {(experience||[]).map((ex, idx) => (
                    <div key={idx} className="experience-item">
                      <h4>{ex.title || ex.role || 'Role'}</h4>
                      <div className="company">{ex.company||'Company'}</div>
                      <div className="duration">{(ex.startDate||'') + (ex.endDate?` - ${ex.endDate}`:'')}</div>
                      <p style={{color:'#666', marginTop:'10px'}}>{ex.description||''}</p>
                    </div>
                  ))}
                </div>

                {/* Education */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faGraduationCap}/> Education</h3>
                  </div>
                  {(education||[]).length === 0 && <div className="empty-text">No education added</div>}
                  {(education||[]).map((ed, idx) => (
                    <div key={idx} className="education-item">
                      <h4>{ed.degree || 'Degree'}{ed.field?` in ${ed.field}`:''}</h4>
                      <div className="institution">{ed.school || 'Institution'}</div>
                      <div className="duration">{(ed.startDate||'') + (ed.endDate?` - ${ed.endDate}`:'')}</div>
                    </div>
                  ))}
                </div>

                {/* Certifications */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faCertificate}/> Certifications & Awards</h3>
                  </div>
                  {(certifications||[]).map((ct, idx) => (
                    <div key={idx} style={{padding:'15px', background:'#f9fafb', borderRadius:'8px', marginBottom:'12px'}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <div>
                          <h4>{ct.name||'Certification'}</h4>
                          <p style={{color:'#666', fontSize:'14px', marginTop:'5px'}}>{(ct.issuer||'') + (ct.date?` - ${ct.date}`:'')}</p>
                        </div>
                        <span className="status-badge status-offered">Verified</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Languages */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faLanguage}/> Languages</h3>
                  </div>
                  <div className="profile-info-grid">
                    {(languages||[]).map((lg, idx)=> (
                      <div key={idx}>
                        <h4 style={{marginBottom:'10px'}}>{lg.language||''}</h4>
                        <p style={{color:'#666'}}>{lg.proficiency||''}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Social Links */}
                <div className="card" style={{margin:'20px', ...elevatedCardStyle}}>
                  <div className="card-header">
                    <h3 className="card-title"><FontAwesomeIcon icon={faLink}/> Social Links</h3>
                  </div>
                  <div className="social-links">
                    {(professionalLinks||[]).map((ln, idx)=> (
                      <a href={ln.url||'#'} key={idx} className="social-link" target="_blank" rel="noreferrer">
                        <FontAwesomeIcon icon={faLink}/> <span>{ln.label||ln.url}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Original compact form grid retained (for edit field coverage) */}
                <div className="profile-form-grid profile-row" style={{margin:'20px'}}>
                  <div>
                    <label>First Name</label>
                    <input name="firstName" value={profileForm.firstName} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Last Name</label>
                    <input name="lastName" value={profileForm.lastName} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Email</label>
                    <input name="email" value={profileForm.email} readOnly className="filter-input" />
                  </div>
                  <div>
                    <label>Phone Number</label>
                    <input name="phoneNumber" value={profileForm.phoneNumber} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div style={{gridColumn:'1 / -1'}}>
                    <label>Location</label>
                    <input name="location" value={profileForm.location} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Professional Title</label>
                    <input name="professionalTitle" value={profileForm.professionalTitle} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Years of Experience</label>
                    <input name="yearsOfExperience" value={profileForm.yearsOfExperience} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Career Level</label>
                    <input name="careerLevel" value={profileForm.careerLevel} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Availability</label>
                    <input name="availability" value={profileForm.availability} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Expected Salary</label>
                    <input name="expectedSalary" value={profileForm.expectedSalary} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div>
                    <label>Currency</label>
                    <input name="currency" value={profileForm.currency} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div style={{gridColumn:'1 / -1'}}>
                    <label>Professional Summary</label>
                    <textarea name="professionalSummary" value={profileForm.professionalSummary} onChange={handleProfileChange} className="filter-input" rows={4} />
                  </div>
                  <div style={{gridColumn:'1 / -1'}}>
                    <label>Skills (comma separated)</label>
                    <input name="skills" value={profileForm.skills} onChange={handleProfileChange} className="filter-input" />
                  </div>
                  <div style={{gridColumn:'1 / -1'}}>
                    <label>Tools/Software (comma separated)</label>
                    <input name="tools" value={profileForm.tools} onChange={handleProfileChange} className="filter-input" />
                  </div>
                </div>
                {/* Education */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">EDUCATION</div>
                  {education.map((ed, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="School" value={ed.school || ''} onChange={e=>setEducation(education.map((x,i)=>i===idx?{...x,school:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Degree" value={ed.degree || ''} onChange={e=>setEducation(education.map((x,i)=>i===idx?{...x,degree:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Field" value={ed.field || ''} onChange={e=>setEducation(education.map((x,i)=>i===idx?{...x,field:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Start" value={ed.startDate || ''} onChange={e=>setEducation(education.map((x,i)=>i===idx?{...x,startDate:e.target.value}:x))} className="filter-input" />
                      <input placeholder="End" value={ed.endDate || ''} onChange={e=>setEducation(education.map((x,i)=>i===idx?{...x,endDate:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setEducation(education.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setEducation([...education, {school:'',degree:'',field:'',startDate:'',endDate:''}])}>Add Education</button>
                </div>
                {/* Experience */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">WORK EXPERIENCE</div>
                  {experience.map((ex, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="Company" value={ex.company || ''} onChange={e=>setExperience(experience.map((x,i)=>i===idx?{...x,company:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Title" value={ex.title || ex.role || ''} onChange={e=>setExperience(experience.map((x,i)=>i===idx?{...x,title:e.target.value,role:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Start" value={ex.startDate || ''} onChange={e=>setExperience(experience.map((x,i)=>i===idx?{...x,startDate:e.target.value}:x))} className="filter-input" />
                      <input placeholder="End" value={ex.endDate || ''} onChange={e=>setExperience(experience.map((x,i)=>i===idx?{...x,endDate:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Description" value={ex.description || ''} onChange={e=>setExperience(experience.map((x,i)=>i===idx?{...x,description:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setExperience(experience.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setExperience([...experience, {company:'',title:'',startDate:'',endDate:'',description:''}])}>Add Experience</button>
                </div>
                {/* Languages */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">LANGUAGES</div>
                  {languages.map((lg, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="Language" value={lg.language || ''} onChange={e=>setLanguages(languages.map((x,i)=>i===idx?{...x,language:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Proficiency" value={lg.proficiency || ''} onChange={e=>setLanguages(languages.map((x,i)=>i===idx?{...x,proficiency:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setLanguages(languages.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setLanguages([...languages, {language:'', proficiency:''}])}>Add Language</button>
                </div>
                {/* Certifications */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">CERTIFICATIONS</div>
                  {certifications.map((ct, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="Certification" value={ct.name || ''} onChange={e=>setCertifications(certifications.map((x,i)=>i===idx?{...x,name:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Issuer" value={ct.issuer || ''} onChange={e=>setCertifications(certifications.map((x,i)=>i===idx?{...x,issuer:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Date" value={ct.date || ''} onChange={e=>setCertifications(certifications.map((x,i)=>i===idx?{...x,date:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setCertifications(certifications.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setCertifications([...certifications, {name:'', issuer:'', date:''}])}>Add Certification</button>
                </div>
                {/* Professional Links */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">PROFESSIONAL LINKS</div>
                  {professionalLinks.map((ln, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="Label (e.g., LinkedIn)" value={ln.label || ''} onChange={e=>setProfessionalLinks(professionalLinks.map((x,i)=>i===idx?{...x,label:e.target.value}:x))} className="filter-input" />
                      <input placeholder="URL" value={ln.url || ''} onChange={e=>setProfessionalLinks(professionalLinks.map((x,i)=>i===idx?{...x,url:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setProfessionalLinks(professionalLinks.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setProfessionalLinks([...professionalLinks, {label:'', url:''}])}>Add Link</button>
                </div>
                {/* References */}
                <div style={{marginTop:'22px'}}>
                  <div className="profile-section-title">REFERENCES</div>
                  {references.map((rf, idx) => (
                    <div key={idx} className="profile-subgrid" style={{marginBottom:'10px'}}>
                      <input placeholder="Name" value={rf.name || ''} onChange={e=>setReferences(references.map((x,i)=>i===idx?{...x,name:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Contact" value={rf.contact || ''} onChange={e=>setReferences(references.map((x,i)=>i===idx?{...x,contact:e.target.value}:x))} className="filter-input" />
                      <input placeholder="Relation" value={rf.relation || ''} onChange={e=>setReferences(references.map((x,i)=>i===idx?{...x,relation:e.target.value}:x))} className="filter-input" />
                      <button className="btn btn-secondary btn-sm" onClick={()=>setReferences(references.filter((_,i)=>i!==idx))}>Remove</button>
                    </div>
                  ))}
                  <button className="btn btn-primary btn-sm" onClick={()=>setReferences([...references, {name:'', contact:'', relation:''}])}>Add Reference</button>
                </div>
                <div style={{marginTop:'16px', display:'flex', gap:'10px'}}>
                  <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={savingProfile}>
                    {savingProfile ? 'Saving...' : 'Save Profile'}
                  </button>
                  {profileMessage && <span style={{color:'#4caf50'}}>{profileMessage}</span>}
                </div>
              </div>
            </div>
          )}

          {/* Resume/CV Section - Temporarily Disabled */}
          {/* {activeSection === 'resume' && (
            <div className="card" ref={sectionRefs.current.resume}>
              <div className="card-header">
                <h3 className="card-title">Resume/CV</h3>
              </div>
              <div className="card-content">
                <p>Resume management will be available here.</p>
              </div>
            </div>
          )} */}

          {/* Career Resources Section */}
          {activeSection === 'resources' && (
            <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
            <div className="card" ref={sectionRefs.current.resources}>
              <div className="card-header" style={{ flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <h3 className="card-title" style={{ textAlign: 'center', width: '100%', display: 'block', margin: '0 auto' }}>AI-Recommended Career Resources</h3>
                {dashboardData.careerResources && dashboardData.careerResources.length > 0 && (
                  <p style={{ fontSize: '14px', color: '#64748b', marginTop: '8px', textAlign: 'center', width: '100%' }}>
                    Personalized based on your profile and interests
                  </p>
                )}
              </div>
              <div className="card-content">
                {dashboardData.careerResources && dashboardData.careerResources.length > 0 ? (
                  <div className="resources-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                    gap: '1.5rem' 
                  }}>
                    {dashboardData.careerResources.map((resource, idx) => (
                      <div key={idx} className="resource-card" style={{
                        background: '#fff',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      onClick={() => window.open(resource.url, '_blank')}>
                        <div style={{ display: 'flex', alignItems: 'start', gap: '12px', marginBottom: '12px' }}>
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '8px',
                            background: resource.relevance === 'High' ? 'linear-gradient(135deg, #10b981, #059669)' : 
                                       resource.relevance === 'Medium' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 
                                       'linear-gradient(135deg, #6b7280, #4b5563)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            fontWeight: 'bold',
                            fontSize: '18px'
                          }}>
                            <FontAwesomeIcon icon={faBook} />
                          </div>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ 
                              margin: 0, 
                              marginBottom: '4px', 
                              fontSize: '16px', 
                              fontWeight: '600',
                              color: '#0f172a'
                            }}>
                              {resource.title}
                            </h4>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                              <span style={{
                                background: '#f1f5f9',
                                color: '#475569',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                {resource.type}
                              </span>
                              <span style={{
                                background: '#eff6ff',
                                color: '#3b82f6',
                                padding: '2px 8px',
                                borderRadius: '6px',
                                fontSize: '11px',
                                fontWeight: '600'
                              }}>
                                {resource.platform}
                              </span>
                              {resource.relevance === 'High' && (
                                <span style={{
                                  background: '#dcfce7',
                                  color: '#16a34a',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontSize: '11px',
                                  fontWeight: '600'
                                }}>
                                  Highly Relevant
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '14px', 
                          color: '#64748b',
                          lineHeight: '1.5',
                          marginBottom: '12px'
                        }}>
                          {resource.description}
                        </p>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          paddingTop: '12px',
                          borderTop: '1px solid #e2e8f0'
                        }}>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                            {resource.category}
                          </span>
                          <span style={{
                            fontSize: '12px',
                            color: '#3b82f6',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            View Resource <FontAwesomeIcon icon={faArrowUp} style={{ transform: 'rotate(45deg)' }} />
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <FontAwesomeIcon icon={faBook} className="empty-icon" />
                    <h3>No Resources Available</h3>
                    <p>Complete your profile to get personalized career resources recommendations.</p>
                    <button className="btn btn-primary" onClick={() => navigate('/profile')}>
                      <FontAwesomeIcon icon={faUser} /> Complete Profile
                    </button>
                  </div>
                )}
              </div>
              </div>
            </div>
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div ref={sectionRefs.current.settings} style={{ width: '100%', margin: 0, padding: 0 }}>
              <SettingsPage />
            </div>
          )}
        </div>
      </div>
      </div>

      {isMobileView && (
        <button
          type="button"
          className="js-dashboard-fab btn-custom-size"
          aria-label={showMobileSidebar ? 'Close menu' : 'Open navigation menu'}
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
        >
          <FontAwesomeIcon icon={showMobileSidebar ? faTimes : faBars} />
        </button>
      )}

      {/* Resume Upload Modal */}
      {showResumeUpload && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <ResumeUpload 
            onResumeUploaded={handleResumeUploaded}
            onClose={() => setShowResumeUpload(false)}
          />
        </div>
      )}
    </>
  );
};

export default JobSeekerDashboard;
